import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ExcelIcon from '../components/ExcelIcon';
import { Button, Cascader, Divider, Dropdown, IconButton, InputNumber, Message, Panel, Popover, SelectPicker, Stack, toaster, Tooltip, Whisper } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import ProviderBadge from '../components/ProviderBadge';
import ActualsEmailAlertsModal from '../components/ActualsEmailAlertsModal';
import ActualsPaceModal from '../components/ActualsPaceModal';
import ProviderLinkModal from '../components/ProviderLinkModal';
import usePermissions from '../hooks/usePermissions';
import './TaskActualsPage.css';
import './ActualsPrint.css';
import './ActualsPage.css';

import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import API_CONFIG from '../config/apiConfig';

import {
  useTask,
  useTasks,
  useUpdateTask,
  useTaskHourForecastForMonths,
  useTaskActualsForMonths,
  useClearTaskActualsForMonth,
  useActiveProvider,
  useTaskOrders,
  useUsers,
  useCloseDates,
  usePrefetchTaskActualsMonths,
} from '../hooks/useQueries';
import {
  MonthSlideCells,
  getMonthSlideFetchMonths,
  getMonthSlidePrefetchMonthLists,
  monthSlideColumns,
  useMonthSlide,
} from '../components/MonthSlide';

import { formatHours } from '../utils/chartUtils';
import { exportSheetsToXlsx } from '../utils/excelExport';
import { buildMonthRangeInclusive } from '../utils/monthRange';

const isIsoDate = (s) => typeof s === 'string' && /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(s);

const tryParseJsonText = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const isProbablyHtml = (text) => /^\s*<!doctype\s+html/i.test(String(text || '')) || /^\s*<html/i.test(String(text || ''));

const parseTimecampTasksJson = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { tasks: [], error: 'Unexpected JSON shape from TimeCamp (expected object keyed by task_id).' };
  }

  const rows = Object.values(payload);
  const tasks = rows
    .map((r) => {
      const taskId = Number(r?.task_id);
      const parentId = Number(r?.parent_id);
      const hasChildren = r?.hasChildren === true;
      return {
        taskId: Number.isFinite(taskId) ? taskId : null,
        parentId: Number.isFinite(parentId) ? parentId : 0,
        name: String(r?.name || '—'),
        hasChildren,
        raw: r,
      };
    })
    .filter((t) => Number.isInteger(t.taskId));

  // If the API doesn't send hasChildren, infer it from parent relationships.
  const inferredHasChildren = new Set();
  for (const t of tasks) {
    const pid = Number(t.parentId);
    if (Number.isInteger(pid) && pid > 0) inferredHasChildren.add(pid);
  }
  for (const t of tasks) {
    if (!t.hasChildren && inferredHasChildren.has(Number(t.taskId))) {
      t.hasChildren = true;
    }
  }

  return { tasks, error: null };
};

const buildTimecampTaskTree = (tasks) => {
  const nodesById = new Map();
  const parentById = new Map();

  for (const t of tasks || []) {
    const id = Number(t?.taskId);
    if (!Number.isInteger(id)) continue;
    nodesById.set(id, {
      label: String(t?.name || '—'),
      value: id,
      task: t,
    });
    parentById.set(id, Number.isInteger(Number(t?.parentId)) ? Number(t.parentId) : 0);
  }

  const roots = [];
  for (const [id, node] of nodesById.entries()) {
    const parentId = parentById.get(id) ?? 0;
    const parentNode = nodesById.get(parentId);
    if (!parentId || !parentNode) {
      roots.push(node);
      continue;
    }
    if (!Array.isArray(parentNode.children)) parentNode.children = [];
    parentNode.children.push(node);
  }

  const sortTree = (nodes) => {
    nodes.sort((a, b) => String(a?.label || '').localeCompare(String(b?.label || '')));
    for (const n of nodes) {
      if (Array.isArray(n.children) && n.children.length > 0) sortTree(n.children);
    }
  };
  sortTree(roots);

  return { roots, nodesById, parentById };
};

const buildTimecampPathLabels = (taskId, nodesById, parentById) => {
  const id = Number(taskId);
  if (!Number.isInteger(id)) return [];

  const labels = [];
  let cur = id;
  let guard = 0;
  while (Number.isInteger(cur) && guard < 50) {
    const node = nodesById.get(cur);
    if (!node) break;
    labels.push(node.label);
    const parent = parentById.get(cur) ?? 0;
    if (!parent) break;
    cur = parent;
    guard += 1;
  }

  return labels.reverse();
};

const normalizePersonName = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const findBestUserMatchByName = (timecampName, users) => {
  const tcNorm = normalizePersonName(timecampName);
  if (!tcNorm) return { userId: null, reason: 'no_name' };

  const arr = Array.isArray(users) ? users : [];
  const exact = arr.filter((u) => normalizePersonName(u?.name) === tcNorm);
  if (exact.length === 1) return { userId: Number(exact[0].id), reason: 'exact' };
  if (exact.length > 1) return { userId: null, reason: 'ambiguous_exact' };

  const contains = arr.filter((u) => {
    const un = normalizePersonName(u?.name);
    if (!un) return false;
    return un.includes(tcNorm) || tcNorm.includes(un);
  });
  if (contains.length === 1) return { userId: Number(contains[0].id), reason: 'contains' };
  if (contains.length > 1) return { userId: null, reason: 'ambiguous_contains' };

  return { userId: null, reason: 'no_match' };
};

const resolveApiBaseUrl = () => {
  const raw = String(API_CONFIG?.BASE_URL || '').trim();
  if (!raw) return '/api';

  // Normalize: callers sometimes configure BASE_URL as "http://localhost:3001" instead of ".../api".
  const withoutTrailing = raw.replace(/\/+$/, '');
  if (/\/api$/i.test(withoutTrailing)) return withoutTrailing;
  return `${withoutTrailing}/api`;
};

const formatMonthLabel = (monthStart) => {
  if (!isIsoDate(monthStart)) return '—';
  const [y, m] = monthStart.split('-').map((v) => Number(v));
  if (!Number.isInteger(y) || !Number.isInteger(m)) return '—';
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString(undefined, { month: 'short', year: '2-digit' });
};

const toMonthStartIso = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const getCurrentYearStartMonthIso = () => {
  const now = new Date();
  const startMonthIndex = now.getMonth() < 6 ? 0 : 6;
  return toMonthStartIso(new Date(now.getFullYear(), startMonthIndex, 1));
};

const addMonthsIso = (monthStart, deltaMonths) => {
  if (!isIsoDate(monthStart)) return null;
  const y = Number(String(monthStart).slice(0, 4));
  const m0 = Number(String(monthStart).slice(5, 7)) - 1;
  if (!Number.isInteger(y) || !Number.isInteger(m0)) return null;
  const d = new Date(y, m0 + Number(deltaMonths || 0), 1);
  d.setHours(0, 0, 0, 0);
  return toMonthStartIso(d);
};

const roundToQuarterHour = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n / 0.25) * 0.25;
};

const formatQuarterHours = (value) => {
  const r = roundToQuarterHour(value);
  return r.toFixed(2);
};

const formatDeltaHours = (delta) => {
  const n = Number(delta);
  if (!Number.isFinite(n)) return '';
  const r = roundToQuarterHour(n);
  if (Math.abs(r) < 0.01) return '0.00';
  const s = formatQuarterHours(r);
  return r > 0 ? `+${s}` : s;
};

const normalizeIsoTimestamp = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const latestIsoTimestamp = (a, b) => {
  const aa = normalizeIsoTimestamp(a);
  const bb = normalizeIsoTimestamp(b);
  if (aa && bb) return aa >= bb ? aa : bb;
  return aa || bb || null;
};

const formatDaysAgoLabel = (value) => {
  const iso = normalizeIsoTimestamp(value);
  if (!iso) return null;

  const then = new Date(iso);
  const now = new Date();
  const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = nowDay.getTime() - thenDay.getTime();
  const days = Number.isFinite(diffMs) ? Math.max(0, Math.floor(diffMs / (24 * 60 * 60 * 1000))) : null;
  if (!Number.isInteger(days)) return null;
  if (days === 0) return 'today';

  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const formatRefreshedLabel = (value) => {
  const iso = normalizeIsoTimestamp(value);
  if (!iso) return null;
  const dayLabel = formatDaysAgoLabel(iso);
  if (!dayLabel) return null;
  if (dayLabel === 'today') {
    const t = new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `today at ${t}`;
  }
  return dayLabel;
};

const countWeekdaysInRange = (startInclusive, endExclusive) => {
  if (!(startInclusive instanceof Date) || !(endExclusive instanceof Date)) return 0;

  const start = new Date(startInclusive.getFullYear(), startInclusive.getMonth(), startInclusive.getDate());
  const end = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (start.getTime() >= end.getTime()) return 0;

  let count = 0;
  const cursor = new Date(start);
  while (cursor.getTime() < end.getTime()) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const parseMonthDay = (raw) => {
  const s = String(raw ?? '').slice(0, 10);
  const m = s.match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { month: Number(m[1]), day: Number(m[2]) };
};

const countCloseDatesInRange = (startInclusive, endExclusive, rows) => {
  if (!(startInclusive instanceof Date) || !(endExclusive instanceof Date)) return 0;
  const list = Array.isArray(rows) ? rows : [];

  const start = new Date(startInclusive.getFullYear(), startInclusive.getMonth(), startInclusive.getDate());
  const end = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (start.getTime() >= end.getTime()) return 0;

  const years = new Set();
  for (let y = start.getFullYear(); y <= end.getFullYear(); y += 1) years.add(y);

  let count = 0;
  for (const r of list) {
    const md = parseMonthDay(r?.closeDate ?? r?.close_date);
    if (!md || !Number.isFinite(md.month) || !Number.isFinite(md.day)) continue;

    for (const y of years) {
      const d = new Date(y, md.month - 1, md.day);
      d.setHours(0, 0, 0, 0);
      if (d.getTime() >= start.getTime() && d.getTime() < end.getTime()) {
        count += 1;
      }
    }
  }

  return count;
};

const shouldWarnProjectedShortfall = ({ monthStart, projected, actual, currentMonthStart, ftePercent, closeDates }) => {
  if (String(monthStart) !== String(currentMonthStart)) return false;

  const proj = Number(projected);
  const act = Number(actual);
  if (!Number.isFinite(proj) || proj <= 0) return false;
  if (!Number.isFinite(act)) return false;

  const minTarget = proj * 0.95;
  if (act >= minTarget) return false;

  const y = Number(String(monthStart).slice(0, 4));
  const m0 = Number(String(monthStart).slice(5, 7)) - 1;
  if (!Number.isInteger(y) || !Number.isInteger(m0)) return false;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  today.setHours(0, 0, 0, 0);

  const monthStartDate = new Date(y, m0, 1);
  monthStartDate.setHours(0, 0, 0, 0);
  const monthEndExclusive = new Date(y, m0 + 1, 1);
  monthEndExclusive.setHours(0, 0, 0, 0);

  const rangeStart = today.getTime() > monthStartDate.getTime() ? today : monthStartDate;
  if (rangeStart.getTime() >= monthEndExclusive.getTime()) return false;

  const weekdaysRemaining = countWeekdaysInRange(rangeStart, monthEndExclusive);
  const closeDaysRemaining = countCloseDatesInRange(rangeStart, monthEndExclusive, closeDates);

  const rawFte = Number(ftePercent);
  const fteScale = Number.isFinite(rawFte) ? Math.max(0, rawFte) / 100 : 1;
  const remainingCapacity = Math.max(0, weekdaysRemaining * 8 - closeDaysRemaining * 8) * fteScale;

  const maxReachable = act + remainingCapacity;
  return maxReachable < minTarget;
};

const DeltaBadge = ({ delta, projected, actual, shortfallWarning }) => {
  const n = Number(delta);
  if (!Number.isFinite(n)) return null;

  const proj = Number(projected);
  const act = Number(actual);

  const isProjZero = !Number.isFinite(proj) || Math.abs(proj) < 0.01;
  const isActZero = !Number.isFinite(act) || Math.abs(act) < 0.01;

  let bg = '#fffbe6';
  let border = '#ffe58f';
  let color = '#ad8b00';
  let showOverageWarning = false;

  if (isProjZero && isActZero) {
    bg = '#f6ffed';
    border = '#b7eb8f';
    color = '#389e0d';
  } else if (isProjZero && !isActZero) {
    bg = '#fff1f0';
    border = '#ffa39e';
    color = '#cf1322';
    showOverageWarning = true;
  } else {
    const pct = ((act - proj) / Math.abs(proj)) * 100;
    const absPct = Math.abs(pct);

    if (absPct <= 5) {
      bg = '#f6ffed';
      border = '#b7eb8f';
      color = '#389e0d';
    } else if (pct > 10) {
      bg = '#fff1f0';
      border = '#ffa39e';
      color = '#cf1322';
      showOverageWarning = true;
    }
  }

  const warningMessage = showOverageWarning
    ? 'Actual hours are > 110% of the projected hours.'
    : shortfallWarning
      ? 'User will likely not be able to meet the projected hours based on available time in the month.'
      : null;
  const warningColor = showOverageWarning ? '#cf1322' : '#fa8c16';
  const badgeContent = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1px 6px',
        borderRadius: 999,
        border: `1px solid ${border}`,
        background: bg,
        color,
        fontSize: 11,
        fontWeight: 800,
        lineHeight: '16px',
        whiteSpace: 'nowrap',
      }}
    >
      {formatDeltaHours(n)}
      {warningMessage ? (
        <span
          aria-label="Hours overage warning"
          style={{ color: warningColor, marginLeft: 4, fontSize: 12, fontWeight: 900, lineHeight: 1 }}
        >
          !
        </span>
      ) : null}
    </span>
  );

  if (!warningMessage) return badgeContent;

  return (
    <Whisper speaker={<Tooltip>{warningMessage}</Tooltip>} trigger={['hover', 'focus']} placement="top">
      {badgeContent}
    </Whisper>
  );
};


function TaskPeopleMonthlyLayout({
  taskId,
  task,
  projectId,
  taskOrderId,
  timecampTaskId,
  activeProviderName,
  provider,
  providerLoading,
  autoPullMonthStart,
  taskForecastPath,
  onOpenTaskForecast,
  actualsReturnPath,
}) {
  const WINDOW_SIZE = 6;

  const isTimeCampActive = activeProviderName === 'timecamp';
  const allowManualEdit = activeProviderName === 'manual' || activeProviderName === null;
  const isOdcTask = String(task?.taskType || '').trim().toLowerCase() === 'odc';

  // Any provider that pulls from an upstream system gets the pull/refresh
  // controls. Only 'manual' (and no provider at all) keeps hours locally.
  const isPullProviderActive = !!activeProviderName && activeProviderName !== 'manual';
  const providerLabel = provider?.displayName || activeProviderName || 'the hours provider';

  const currentYearStart = useMemo(() => getCurrentYearStartMonthIso(), []);
  const [startMonth, setStartMonth] = useState(() => getCurrentYearStartMonthIso());

  const [printMode, setPrintMode] = useState(false);
  const [alertsModalOpen, setAlertsModalOpen] = useState(false);
  const [paceModalOpen, setPaceModalOpen] = useState(false);

  useEffect(() => {
    if (!printMode) return undefined;

    const handleAfterPrint = () => setPrintMode(false);
    window.addEventListener('afterprint', handleAfterPrint);

    const t = setTimeout(() => {
      try {
        window.print();
      } catch {
        setPrintMode(false);
      }
    }, 60);

    return () => {
      clearTimeout(t);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [printMode]);

  const months = useMemo(() => {
    const list = [];
    for (let i = 0; i < WINDOW_SIZE; i += 1) {
      const ms = addMonthsIso(startMonth, i);
      if (ms) list.push(ms);
    }
    return list;
  }, [WINDOW_SIZE, startMonth]);

  // The months either side of the window, parked just out of view for the month slide.
  const prevMonth = useMemo(() => addMonthsIso(startMonth, -1), [startMonth]);
  const nextMonth = useMemo(() => addMonthsIso(startMonth, WINDOW_SIZE), [WINDOW_SIZE, startMonth]);

  // Whole years around the window, so stepping a month reuses data that is already loaded.
  const fetchMonths = useMemo(() => getMonthSlideFetchMonths(startMonth, WINDOW_SIZE), [WINDOW_SIZE, startMonth]);

  const {
    data: taskHourForecast,
    isLoading: taskHourForecastLoading,
    error: taskHourForecastError,
  } = useTaskHourForecastForMonths(Number.isInteger(taskId) ? taskId : null, fetchMonths);

  const forecastHoursByUserId = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(taskHourForecast?.rows) ? taskHourForecast.rows : [];
    for (const r of rows) {
      const uid = Number(r?.userId);
      if (!Number.isInteger(uid)) continue;
      map.set(uid, r?.hours && typeof r.hours === 'object' ? r.hours : {});
    }
    return map;
  }, [taskHourForecast?.rows]);

  const {
    data: taskActuals,
    isLoading: taskActualsLoading,
    error: taskActualsError,
    refetch: refetchTaskActuals,
  } = useTaskActualsForMonths(Number.isInteger(taskId) ? taskId : null, fetchMonths);

  const actualHoursByUserId = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(taskActuals?.rows) ? taskActuals.rows : [];
    for (const r of rows) {
      const uid = Number(r?.userId);
      if (!Number.isInteger(uid)) continue;
      map.set(uid, r?.hours && typeof r.hours === 'object' ? r.hours : {});
    }
    return map;
  }, [taskActuals?.rows]);

  const monthLastModifiedByMonth = useMemo(() => {
    const map = {};
    const raw = taskActuals?.monthLastModifiedByMonth;
    if (!raw || typeof raw !== 'object') return map;

    for (const [monthStartRaw, tsRaw] of Object.entries(raw)) {
      const monthStartText = String(monthStartRaw || '').slice(0, 10);
      const monthStart = isIsoDate(monthStartText) ? `${monthStartText.slice(0, 7)}-01` : null;
      const ts = normalizeIsoTimestamp(tsRaw);
      if (!monthStart || !ts) continue;
      const prev = map[monthStart];
      if (!prev || ts > prev) map[monthStart] = ts;
    }

    return map;
  }, [taskActuals?.monthLastModifiedByMonth]);

  const people = useMemo(() => {
    const byId = new Map();
    const assignedUserIds = new Set();
    const add = (userId, name, role, timecardLastModifiedAt = null) => {
      const id = Number.isInteger(Number(userId)) ? Number(userId) : null;
      const key = id !== null ? `id:${id}` : `name:${String(name || '').trim()}`;
      if (!key || key === 'name:') return;

      const normalizedLastModified = normalizeIsoTimestamp(timecardLastModifiedAt);

      if (!byId.has(key)) {
        byId.set(key, {
          userId: id,
          name: String(name || '—'),
          roles: new Set(role ? [role] : []),
          notAssigned: false,
          timecardLastModifiedAt: normalizedLastModified,
        });
        if (id !== null && role) assignedUserIds.add(id);
        return;
      }
      const existing = byId.get(key);
      if (role) existing.roles.add(role);
      if ((!existing.name || existing.name === '—') && name) existing.name = String(name);
      if (normalizedLastModified) {
        const prev = normalizeIsoTimestamp(existing.timecardLastModifiedAt);
        if (!prev || normalizedLastModified > prev) {
          existing.timecardLastModifiedAt = normalizedLastModified;
        }
      }
      if (id !== null && role) assignedUserIds.add(id);
    };

    const activities = Array.isArray(task?.activities) ? task.activities : [];
    for (const a of activities) {
      if (a?.leadUserId || a?.leadName) add(a?.leadUserId, a?.leadName, 'lead');
      const supporters = Array.isArray(a?.supporters) ? a.supporters : [];
      for (const s of supporters) add(s?.userId, s?.name, 'support');
    }

    // Include anyone with actuals in the displayed months, even if they're not on the roster.
    // This is required to surface accepted users that are "Not assigned to task". (The fetch covers
    // whole years, so rows for people with hours only in other months are left out.)
    const actualRows = (Array.isArray(taskActuals?.rows) ? taskActuals.rows : []).filter((r) =>
      months.some((m) => Number(r?.hours?.[m] ?? 0) !== 0)
    );
    for (const r of actualRows) {
      const uid = Number(r?.userId);
      if (!Number.isInteger(uid)) continue;
      const name = String(r?.userName || '').trim() || `User ${uid}`;
      add(uid, name, null, r?.timecardLastModifiedAt);
      const key = `id:${uid}`;
      const rec = byId.get(key);
      if (rec && !assignedUserIds.has(uid)) {
        rec.notAssigned = true;
      }
    }

    // If we still have no people, show a single placeholder row.
    const arr = Array.from(byId.values());
    if (arr.length === 0) {
      return [{ userId: null, name: '—', roles: new Set(), notAssigned: false }];
    }
    arr.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
    return arr;
  }, [months, task?.activities, taskActuals?.rows]);

  const colTemplate = useMemo(() => monthSlideColumns('minmax(110px, 1fr)', months, 'minmax(150px, 1fr)'), [months]);

  // TimeCamp keeps its mapping on the task row; every other provider stores it in
  // task_provider_links and reports it back as isLinkedToActiveProvider.
  const isLinkedToProvider = isTimeCampActive
    ? Number.isInteger(timecampTaskId) && Number(timecampTaskId) > 0
    : task?.isLinkedToActiveProvider === true;

  const canPull = !isOdcTask && isLinkedToProvider;
  // Pulling actual hours needs Forecast hours on the project; editing them by hand and remediating
  // unmatched hours need Manage Finance.
  const permissions = usePermissions();
  const canPullHours = permissions.canProject(projectId, 'disciplines');
  const canFinance = permissions.canProject(projectId, 'finance');
  const currentMonthStart = useMemo(() => toMonthStartIso(new Date()), []);
  const isOnCurrentWindow = startMonth === currentYearStart;

  const prefetchTaskActualsMonths = usePrefetchTaskActualsMonths();

  // Callback ref: the toolbar may mount after first render (loading states), and the sticky header
  // offset has to track its real height.
  const [toolbarEl, setToolbarEl] = useState(null);
  const [toolbarH, setToolbarH] = useState(0);
  useLayoutEffect(() => {
    if (!toolbarEl) return undefined;
    const measure = () => setToolbarH(Math.ceil(toolbarEl.getBoundingClientRect().height));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(toolbarEl);
    return () => ro.disconnect();
  }, [toolbarEl]);

  const { containerRef: monthSlideContainerRef, stepMonths } = useMonthSlide({
    start: startMonth,
    setStart: setStartMonth,
    prepare: (target) => prefetchTaskActualsMonths(taskId, getMonthSlideFetchMonths(target, WINDOW_SIZE)),
  });

  // Keep the months a few steps either way loaded, so stepping through them never waits on the network.
  useEffect(() => {
    for (const list of getMonthSlidePrefetchMonthLists(startMonth, WINDOW_SIZE)) {
      prefetchTaskActualsMonths(taskId, list);
    }
  }, [WINDOW_SIZE, prefetchTaskActualsMonths, startMonth, taskId]);

  const goPrevMonths = () => stepMonths(-1);
  const goNextMonths = () => stepMonths(1);

  const goCurrentMonths = () => {
    setStartMonth(currentYearStart);
  };

  const monthRangeLabel = useMemo(() => {
    if (months.length === 0) return '';
    const first = formatMonthLabel(months[0]);
    const last = formatMonthLabel(months[months.length - 1]);
    return first === last ? first : `${first} – ${last}`;
  }, [months]);

  const authenticatedFetch = useAuthenticatedFetch();
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportStartMonth, setExportStartMonth] = useState(() => getCurrentYearStartMonthIso());
  const [exportEndMonth, setExportEndMonth] = useState(() => addMonthsIso(getCurrentYearStartMonthIso(), WINDOW_SIZE - 1) || getCurrentYearStartMonthIso());

  const exportMonthOptions = useMemo(() => {
    const nowMonthStart = toMonthStartIso(new Date());
    const first = '2010-01-01';
    const last = addMonthsIso(nowMonthStart, 120) || nowMonthStart;
    return buildMonthRangeInclusive(first, last, 360).map((m) => ({
      label: formatMonthLabel(m),
      value: m,
    }));
  }, []);

  const selectedExportMonths = useMemo(
    () => buildMonthRangeInclusive(exportStartMonth, exportEndMonth, 240),
    [exportEndMonth, exportStartMonth]
  );

  const selectedExportRangeLabel = useMemo(() => {
    if (selectedExportMonths.length === 0) return '';
    const first = formatMonthLabel(selectedExportMonths[0]);
    const last = formatMonthLabel(selectedExportMonths[selectedExportMonths.length - 1]);
    return first === last ? first : `${first} – ${last}`;
  }, [selectedExportMonths]);

  useEffect(() => {
    if (!exportModalOpen) return;
    const first = months[0] || currentYearStart;
    const last = months[months.length - 1] || first;
    setExportStartMonth(first);
    setExportEndMonth(last);
  }, [currentYearStart, exportModalOpen, months]);

  const requestApiJson = useCallback(
    async (endpoint) => {
      const apiBase = resolveApiBaseUrl();
      const url = `${apiBase.replace(/\/+$/, '')}${endpoint}`;
      const response = await authenticatedFetch(url);
      const text = await response.text();
      const json = text
        ? (() => {
            try {
              return JSON.parse(text);
            } catch {
              return null;
            }
          })()
        : null;

      if (!response.ok) {
        throw new Error(json?.error || text || `Request failed (${response.status})`);
      }

      return json && typeof json === 'object' ? json : {};
    },
    [authenticatedFetch]
  );

  const exportActualsForMonths = useCallback(
    async (exportMonths) => {
      if (!Number.isInteger(taskId) || !Array.isArray(exportMonths) || exportMonths.length === 0) {
        throw new Error('Please select a valid month range before exporting.');
      }

      const monthsKey = exportMonths.join(',');
      const [forecastExport, actualsExport] = await Promise.all([
        requestApiJson(`/tasks/${taskId}/hour-forecast?months=${encodeURIComponent(monthsKey)}`),
        requestApiJson(`/tasks/${taskId}/actuals?months=${encodeURIComponent(monthsKey)}`),
      ]);

      const forecastByUserId = new Map();
      for (const r of Array.isArray(forecastExport?.rows) ? forecastExport.rows : []) {
        const uid = Number(r?.userId);
        if (!Number.isInteger(uid)) continue;
        forecastByUserId.set(uid, r?.hours && typeof r.hours === 'object' ? r.hours : {});
      }

      const actualByUserId = new Map();
      for (const r of Array.isArray(actualsExport?.rows) ? actualsExport.rows : []) {
        const uid = Number(r?.userId);
        if (!Number.isInteger(uid)) continue;
        actualByUserId.set(uid, r?.hours && typeof r.hours === 'object' ? r.hours : {});
      }

      const exportPeopleByKey = new Map();
      for (const p of Array.isArray(people) ? people : []) {
        const uid = Number(p?.userId);
        const key = Number.isInteger(uid) ? `id:${uid}` : `name:${String(p?.name || '').trim()}`;
        if (!key || key === 'name:') continue;
        exportPeopleByKey.set(key, { ...p, userId: Number.isInteger(uid) ? uid : null });
      }

      const addExportRows = (rows) => {
        const list = Array.isArray(rows) ? rows : [];
        for (const r of list) {
          const uid = Number(r?.userId);
          if (!Number.isInteger(uid)) continue;
          const key = `id:${uid}`;
          if (exportPeopleByKey.has(key)) continue;
          exportPeopleByKey.set(key, {
            userId: uid,
            name: String(r?.userName || `User ${uid}`),
            notAssigned: false,
          });
        }
      };

      addExportRows(forecastExport?.rows);
      addExportRows(actualsExport?.rows);

      const exportPeople = Array.from(exportPeopleByKey.values()).sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));

      const safeTaskName = String(task?.task || task?.name || `Task ${taskId}` || 'Task').trim() || 'Task';
      const firstMonthLabel = formatMonthLabel(exportMonths[0]);
      const lastMonthLabel = formatMonthLabel(exportMonths[exportMonths.length - 1]);
      const exportRangeLabel = firstMonthLabel === lastMonthLabel ? firstMonthLabel : `${firstMonthLabel} – ${lastMonthLabel}`;
      const fileName = `${safeTaskName} - Actuals - ${exportRangeLabel}.xlsx`;

      const columns = [{ header: 'Person', key: 'person', width: 28 }];
      for (const m of exportMonths) {
        const label = formatMonthLabel(m);
        columns.push({ header: `${label} Proj`, key: `proj:${m}`, width: 12, numFmt: '0.00' });
        columns.push({ header: `${label} Act`, key: `act:${m}`, width: 12, numFmt: '0.00' });
      }

      const rowsOut = [];
      for (const p of exportPeople) {
        const name = String(p?.name || '—');
        const note = p?.notAssigned ? ' (Not assigned to task)' : '';
        const personLabel = `${name}${note}`;

        const uid = Number(p?.userId);
        const hasUser = Number.isInteger(uid);

        const row = { person: personLabel };
        for (const m of exportMonths) {
          const proj = hasUser ? Number(forecastByUserId.get(uid)?.[m] ?? 0) : 0;
          const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
          const act = isFutureMonth ? '' : (hasUser ? Number(actualByUserId.get(uid)?.[m] ?? 0) : 0);
          row[`proj:${m}`] = Number.isFinite(proj) ? proj : 0;
          row[`act:${m}`] = act === '' ? '' : (Number.isFinite(Number(act)) ? Number(act) : 0);
        }
        rowsOut.push(row);
      }

      const totalsRow = { person: 'Total' };
      for (const m of exportMonths) {
        const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
        totalsRow[`proj:${m}`] = Number(forecastExport?.totals?.[m] ?? 0);
        totalsRow[`act:${m}`] = isFutureMonth ? '' : Number(actualsExport?.totals?.[m] ?? 0);
      }
      rowsOut.push(totalsRow);

      await exportSheetsToXlsx({
        fileName,
        sheets: [{ name: 'Actuals', columns, rows: rowsOut }],
      });
    },
    [currentMonthStart, people, requestApiJson, task?.name, task?.task, taskId]
  );

  const refreshCurrentAndPreviousActualsForExport = useCallback(async () => {
    if (!canPullHours || !isPullProviderActive || !canPull || !Number.isInteger(taskId)) return;

    const previousMonthStart = addMonthsIso(currentMonthStart, -1);
    const monthsToRefresh = [previousMonthStart, currentMonthStart].filter(
      (monthStart, index, arr) => isIsoDate(monthStart) && arr.indexOf(monthStart) === index
    );

    const apiBase = resolveApiBaseUrl().replace(/\/+$/, '');
    const pullUrl = `${apiBase}/tasks/${encodeURIComponent(String(taskId))}/pull-hours`;

    for (const monthStart of monthsToRefresh) {
      const resp = await authenticatedFetch(pullUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monthStart }),
      });

      const json = await resp.json().catch(() => null);
      if (!resp.ok) {
        throw new Error(json?.error || `Failed to refresh ${providerLabel} hours for ${formatMonthLabel(monthStart)} (${resp.status})`);
      }
    }

    if (typeof refetchTaskActuals === 'function') {
      await refetchTaskActuals();
    }
  }, [authenticatedFetch, canPullHours, canPull, currentMonthStart, isPullProviderActive, providerLabel, refetchTaskActuals, taskId]);

  const handleExportXlsx = useCallback(() => {
    setExportModalOpen(true);
  }, []);

  const handleExportConfirm = useCallback(async () => {
    if (selectedExportMonths.length === 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Choose a valid month range before exporting.
        </Message>,
        { placement: 'topEnd', duration: 3500 }
      );
      return;
    }

    setExporting(true);
    try {
      await refreshCurrentAndPreviousActualsForExport();
      await exportActualsForMonths(selectedExportMonths);
      setExportModalOpen(false);
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          Failed to export actuals: {e?.message || String(e)}
        </Message>,
        { placement: 'topEnd', duration: 7000 }
      );
    } finally {
      setExporting(false);
    }
  }, [exportActualsForMonths, refreshCurrentAndPreviousActualsForExport, selectedExportMonths]);

  const pullHoursUrlBase = useMemo(() => {
    const apiBase = resolveApiBaseUrl();
    return `${apiBase.replace(/\/+$/, '')}/tasks/${encodeURIComponent(String(taskId || ''))}/pull-hours`;
  }, [taskId]);

  const [pullingMonth, setPullingMonth] = useState(null);
  const [refreshAttemptedAtByMonth, setRefreshAttemptedAtByMonth] = useState({});

  const [unmatchedModalOpen, setUnmatchedModalOpen] = useState(false);
  const [unmatchedModalMonth, setUnmatchedModalMonth] = useState(null);
  const [unmatchedModalRows, setUnmatchedModalRows] = useState([]);
  const [unmatchedModalEntries, setUnmatchedModalEntries] = useState([]);
  const [unmatchedRemediationByTimecampName, setUnmatchedRemediationByTimecampName] = useState({});

  const { data: usersRes } = useUsers();
  const { data: closeDatesRes } = useCloseDates();

  const closeDates = useMemo(() => {
    const list = Array.isArray(closeDatesRes) ? closeDatesRes : closeDatesRes?.rows;
    return Array.isArray(list) ? list : [];
  }, [closeDatesRes]);

  const usersList = useMemo(() => {
    const list = Array.isArray(usersRes) ? usersRes : usersRes?.users;
    const arr = Array.isArray(list) ? list : [];
    return arr
      .map((u) => ({
        id: Number(u?.id),
        name: String(u?.name || '').trim(),
        email: u?.email ? String(u.email).trim() : '',
        ftePercent: Number(u?.ftePercent ?? u?.fte_percent ?? 100),
      }))
      .filter((u) => Number.isInteger(u.id) && u.name);
  }, [usersRes]);

  const userOptions = useMemo(
    () =>
      usersList
        .map((u) => ({ label: u.name, value: u.id }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [usersList]
  );

  const userNameById = useMemo(() => {
    const map = new Map();
    for (const u of usersList) {
      map.set(Number(u.id), u.name);
    }
    return map;
  }, [usersList]);

  const userEmailById = useMemo(() => {
    const map = new Map();
    for (const u of usersList) {
      map.set(Number(u.id), u.email || '');
    }
    return map;
  }, [usersList]);

  const userFteById = useMemo(() => {
    const map = new Map();
    for (const u of usersList) {
      map.set(Number(u.id), Number.isFinite(Number(u.ftePercent)) ? Number(u.ftePercent) : 100);
    }
    return map;
  }, [usersList]);

  const unmatchedActionOptions = useMemo(
    () => [
      {
        label: 'Select action',
        value: null,
        description: '',
      },
      {
        label: 'Add to task + accept',
        value: 'add',
        description: 'Adds this person to the task roster and pulls their hours into Actuals.',
      },
      {
        label: 'Kick back (send email)',
        value: 'kickback',
        description: 'Sends an email asking them to log time to the right task.',
      },
      {
        label: 'Accept (not assigned to task)',
        value: 'unassigned',
        description: 'Pulls hours into Actuals for the matched user, but does not add them to the task roster.',
      },
    ],
    []
  );

  const getUnmatchedActionLabel = useCallback(
    (action) => {
      const found = unmatchedActionOptions.find((o) => o.value === action);
      return found?.label || 'Select action';
    },
    [unmatchedActionOptions]
  );

  const renderUnmatchedActionMenuFor = useCallback(
    (timecampName) =>
      ({ onClose, left, top, className, ...rest }, ref) => {
        const tc = String(timecampName || '').trim();
        return (
          <Popover
            {...rest}
            ref={ref}
            className={className}
            full
            style={{ left, top, padding: 0, zIndex: 3000 }}
          >
            <Dropdown.Menu
              onSelect={(eventKey) => {
                const v = eventKey;
                const val = v === 'add' || v === 'kickback' || v === 'unassigned' ? v : null;
                setUnmatchedRemediationByTimecampName((prev) => ({
                  ...prev,
                  [tc]: { ...prev?.[tc], action: val, error: null },
                }));
                if (typeof onClose === 'function') onClose();
              }}
            >
              {unmatchedActionOptions
                .filter((o) => o.value !== null)
                .map((opt) => (
                  <Dropdown.Item key={String(opt.value)} eventKey={opt.value} description={opt.description}>
                    {opt.label}
                  </Dropdown.Item>
                ))}
            </Dropdown.Menu>
          </Popover>
        );
      },
    [unmatchedActionOptions]
  );

  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const [matchModalTimecampName, setMatchModalTimecampName] = useState(null);
  const [matchModalUserId, setMatchModalUserId] = useState(null);

  useEffect(() => {
    if (!unmatchedModalOpen) return;

    // Initialize per-user remediation state when the modal opens.
    setUnmatchedRemediationByTimecampName((prev) => {
      const next = { ...prev };
      for (const r of unmatchedModalRows) {
        const tc = String(r?.userName || '').trim();
        if (!tc) continue;
        if (next[tc]) continue;

        const match = findBestUserMatchByName(tc, usersList);
        const suggestedUserId = Number.isInteger(match.userId) ? Number(match.userId) : null;
        next[tc] = {
          // Default to no action selected. (We still prefill the suggested match to reduce clicks.)
          action: null,
          userId: suggestedUserId,
          resolved: false,
          busy: false,
          error: null,
          autoMatched: Boolean(suggestedUserId),
          autoMatchReason: match.reason,
        };
      }
      return next;
    });
  }, [unmatchedModalOpen, unmatchedModalRows, usersList]);

  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [clearMonth, setClearMonth] = useState(null);

  const clearTaskActualsForMonth = useClearTaskActualsForMonth({
    onSuccess: (_data, variables) => {
      toaster.push(
        <Message showIcon type="success" closable>
          Cleared actuals for {formatMonthLabel(variables?.monthStart)}.
        </Message>,
        { placement: 'topEnd', duration: 2400 }
      );
    },
  });

  // Named for TimeCamp historically; the pull endpoint is provider-agnostic and
  // this now drives Clockify too.
  const pullTimecampHoursForMonth = useCallback(
    async (monthStart) => {
      if (isOdcTask) return;
      if (!canPull || !canPullHours) return;
      if (!isIsoDate(monthStart)) return;

      setPullingMonth(monthStart);

      try {
        const resp = await authenticatedFetch(pullHoursUrlBase, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ monthStart }),
        });

        const json = await resp.json().catch(() => null);
        if (!resp.ok) {
          throw new Error(json?.error || `Failed to pull hours (${resp.status})`);
        }

        toaster.push(
          <Message showIcon type="success" closable>
            Hours pulled and saved for {formatMonthLabel(monthStart)}.
          </Message>,
          { placement: 'topEnd', duration: 2400 }
        );

        setRefreshAttemptedAtByMonth((prev) => ({
          ...prev,
          [monthStart]: normalizeIsoTimestamp(json?.refreshedAt) || new Date().toISOString(),
        }));

        const detailsRaw = Array.isArray(json?.unmatchedUsersDetails) ? json.unmatchedUsersDetails : [];
        const details = detailsRaw
          .map((r) => ({
            userName: typeof r?.userName === 'string' ? r.userName.trim() : '',
            hours: Number(r?.hours ?? 0),
          }))
          .filter((r) => r.userName && Number.isFinite(r.hours) && r.hours > 0)
          .sort((a, b) => String(a.userName).localeCompare(String(b.userName)));

        const entriesRaw = Array.isArray(json?.unmatchedEntries) ? json.unmatchedEntries : [];
        const entries = entriesRaw
          .map((e) => ({
            userName: typeof e?.userName === 'string' ? e.userName.trim() : '',
            date: typeof e?.date === 'string' ? e.date.slice(0, 10) : '',
            hours: Number(e?.hours ?? 0),
            description: e?.description ? String(e.description) : '',
            externalId: e?.externalId ? String(e.externalId) : null,
          }))
          .filter((e) => e.userName && isIsoDate(e.date) && Number.isFinite(e.hours) && e.hours > 0);

        if (details.length > 0) {
          setUnmatchedModalMonth(monthStart);
          setUnmatchedModalRows(details);
          setUnmatchedModalEntries(entries);
          setUnmatchedRemediationByTimecampName({});
          // Remediating needs Manage Finance; anyone else sees the warning below instead.
          setUnmatchedModalOpen(canFinance);
        } else {
          const unknownUsers = Array.isArray(json?.unmatchedUsers) ? json.unmatchedUsers : [];
          if (unknownUsers.length > 0) {
            const uniq = Array.from(new Set(unknownUsers)).slice(0, 6);
            toaster.push(
              <Message showIcon type="warning" closable>
                Could not match some TimeCamp users: {uniq.join(', ')}{unknownUsers.length > uniq.length ? '…' : ''}
              </Message>,
              { placement: 'topEnd', duration: 6000 }
            );
          }
        }

        if (typeof refetchTaskActuals === 'function') {
          await refetchTaskActuals();
        }
      } catch (e) {
        toaster.push(
          <Message showIcon type="error" closable>
            Failed to pull TimeCamp entries for {formatMonthLabel(monthStart)}: {e?.message || String(e)}
          </Message>,
          { placement: 'topEnd', duration: 7000 }
        );
      } finally {
        setPullingMonth(null);
      }
    },
    [authenticatedFetch, canFinance, canPullHours, canPull, isOdcTask, pullHoursUrlBase, refetchTaskActuals]
  );

  const didAutoPullRef = useRef(false);
  useEffect(() => {
    if (didAutoPullRef.current) return;
    if (!isIsoDate(autoPullMonthStart)) return;
    if (!isPullProviderActive) return;
    if (!canPull) return;
    if (pullingMonth !== null) return;

    const isFutureMonth = String(autoPullMonthStart).slice(0, 7) > String(currentMonthStart).slice(0, 7);
    if (isFutureMonth) return;

    // Ensure the month is visible in the current window.
    if (!months.includes(autoPullMonthStart)) {
      setStartMonth(autoPullMonthStart);
    }

    didAutoPullRef.current = true;
    pullTimecampHoursForMonth(autoPullMonthStart);
  }, [autoPullMonthStart, canPull, currentMonthStart, isPullProviderActive, months, pullingMonth, pullTimecampHoursForMonth]);

  const acceptUnmatchedUrl = useMemo(() => {
    const apiBase = resolveApiBaseUrl();
    return `${apiBase.replace(/\/+$/, '')}/tasks/${encodeURIComponent(String(taskId || ''))}/actuals/accept-unmatched`;
  }, [taskId]);

  const kickbackSendUrl = useMemo(() => {
    const apiBase = resolveApiBaseUrl();
    return `${apiBase.replace(/\/+$/, '')}/tasks/${encodeURIComponent(String(taskId || ''))}/timecamp/kickback-send`;
  }, [taskId]);

  const totalUnmatchedHours = useMemo(
    () => unmatchedModalRows.reduce((sum, row) => sum + Number(row?.hours ?? 0), 0),
    [unmatchedModalRows]
  );

  const unmatchedProgress = useMemo(() => {
    const total = unmatchedModalRows.length;
    const resolved = unmatchedModalRows.reduce((acc, r) => {
      const tc = String(r?.userName || '').trim();
      if (!tc) return acc;
      const st = unmatchedRemediationByTimecampName?.[tc];
      return acc + (st?.resolved ? 1 : 0);
    }, 0);
    return { total, resolved };
  }, [unmatchedModalRows, unmatchedRemediationByTimecampName]);

  const anyUnmatchedBusy = useMemo(
    () =>
      unmatchedModalRows.some((r) => {
        const tc = String(r?.userName || '').trim();
        return Boolean(unmatchedRemediationByTimecampName?.[tc]?.busy);
      }),
    [unmatchedModalRows, unmatchedRemediationByTimecampName]
  );

  const allUnmatchedResolved = useMemo(
    () =>
      unmatchedModalRows.every((r) => {
        const tc = String(r?.userName || '').trim();
        return Boolean(unmatchedRemediationByTimecampName?.[tc]?.resolved);
      }),
    [unmatchedModalRows, unmatchedRemediationByTimecampName]
  );

  useEffect(() => {
    if (!unmatchedModalOpen) return;
    if (anyUnmatchedBusy) return;
    if (!allUnmatchedResolved) return;
    setUnmatchedModalOpen(false);
  }, [allUnmatchedResolved, anyUnmatchedBusy, unmatchedModalOpen]);

  const applyRemediationForTimecampUser = useCallback(
    async (timecampUserName, options = {}) => {
      if (!unmatchedModalMonth) return;
      const tc = String(timecampUserName || '').trim();
      if (!tc) return;

      const st = unmatchedRemediationByTimecampName?.[tc];
      if (!st || st.resolved || st.busy) return;

      const action = st.action;
      if (!action) {
        setUnmatchedRemediationByTimecampName((prev) => ({
          ...prev,
          [tc]: { ...prev?.[tc], error: 'Select an action first.' },
        }));
        return;
      }

      const row = unmatchedModalRows.find((r) => String(r?.userName || '').trim() === tc);
      const overrideHours = Number(options?.hoursOverride);
      const rowHours = Number.isFinite(overrideHours) ? overrideHours : Number(row?.hours ?? 0);

      setUnmatchedRemediationByTimecampName((prev) => ({
        ...prev,
        [tc]: { ...prev?.[tc], busy: true, error: null },
      }));

      try {
        if (action === 'add') {
          const userId = Number(st.userId);
          if (!Number.isInteger(userId)) {
            throw new Error('Select an app user mapping first.');
          }

          const resp = await authenticatedFetch(pullHoursUrlBase, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              monthStart: unmatchedModalMonth,
              manualUserMappings: [{ timecampUserName: tc, userId }],
            }),
          });

          const json = await resp.json().catch(() => null);
          if (!resp.ok) {
            throw new Error(json?.error || `Failed to pull hours (${resp.status})`);
          }

          const detailsRaw = Array.isArray(json?.unmatchedUsersDetails) ? json.unmatchedUsersDetails : [];
          const details = detailsRaw
            .map((r) => ({
              userName: typeof r?.userName === 'string' ? r.userName.trim() : '',
              hours: Number(r?.hours ?? 0),
            }))
            .filter((r) => r.userName && Number.isFinite(r.hours) && r.hours > 0)
            .sort((a, b) => String(a.userName).localeCompare(String(b.userName)));

          const entriesRaw = Array.isArray(json?.unmatchedEntries) ? json.unmatchedEntries : [];
          const entries = entriesRaw
            .map((e) => ({
              userName: typeof e?.userName === 'string' ? e.userName.trim() : '',
              date: typeof e?.date === 'string' ? e.date.slice(0, 10) : '',
              hours: Number(e?.hours ?? 0),
              description: e?.description ? String(e.description) : '',
              externalId: e?.externalId ? String(e.externalId) : null,
            }))
            .filter((e) => e.userName && isIsoDate(e.date) && Number.isFinite(e.hours) && e.hours > 0);

          const stillUnmatched = details.some((d) => d.userName === tc);
          if (stillUnmatched) {
            throw new Error('Name match still failed after mapping. Double-check the selected app user.');
          }

          const supportersAdded = Number(json?.supportersAdded ?? 0);
          toaster.push(
            <Message showIcon type="success" closable>
              Accepted hours for {tc}{supportersAdded > 0 ? ` (supports added: ${supportersAdded})` : ''}.
            </Message>,
            { placement: 'topEnd', duration: 2600 }
          );

          setUnmatchedModalRows(details);
          setUnmatchedModalEntries(entries);
          setUnmatchedRemediationByTimecampName((prev) => ({
            ...prev,
            [tc]: { ...prev?.[tc], resolved: true, busy: false, error: null },
          }));

          if (typeof refetchTaskActuals === 'function') {
            await refetchTaskActuals();
          }
          return;
        }

        if (action === 'unassigned') {
          const userId = Number(st.userId);
          if (!Number.isInteger(userId)) {
            throw new Error('Select an app user mapping first.');
          }
          if (!Number.isFinite(rowHours) || rowHours <= 0) {
            throw new Error('No hours to accept for this user.');
          }

          const resp = await authenticatedFetch(acceptUnmatchedUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              monthStart: unmatchedModalMonth,
              userId,
              hours: rowHours,
              mode: 'set',
              timecampUserName: tc,
            }),
          });

          const json = await resp.json().catch(() => null);
          if (!resp.ok) {
            throw new Error(json?.error || `Failed to accept unmatched hours (${resp.status})`);
          }

          toaster.push(
            <Message showIcon type="success" closable>
              Accepted {formatHours(rowHours)} for {tc} (not assigned to task).
            </Message>,
            { placement: 'topEnd', duration: 2600 }
          );

          setUnmatchedRemediationByTimecampName((prev) => ({
            ...prev,
            [tc]: { ...prev?.[tc], resolved: true, busy: false, error: null },
          }));

          if (typeof refetchTaskActuals === 'function') {
            await refetchTaskActuals();
          }
          return;
        }

        if (action === 'kickback') {
          const userId = Number(st.userId);
          if (!Number.isInteger(userId)) {
            throw new Error('Select an app user mapping first.');
          }
          const recipientEmail = String(userEmailById.get(userId) || '').trim();
          if (!recipientEmail) {
            throw new Error('Mapped app user is missing an email address.');
          }

          const entries = unmatchedModalEntries.filter((e) => e.userName === tc).slice(0, 200);

          const resp = await authenticatedFetch(kickbackSendUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              monthStart: unmatchedModalMonth,
              recipients: [{ timecampUserName: tc, to: recipientEmail, entries }],
            }),
          });

          const json = await resp.json().catch(() => null);
          if (!resp.ok) {
            throw new Error(json?.error || `Failed to send email (${resp.status})`);
          }

          const sendResults = Array.isArray(json?.sendResults) ? json.sendResults : [];
          const sentForUser = sendResults.find((r) => String(r?.timecampUserName || '').trim() === tc);
          if (!sentForUser?.sent) {
            throw new Error(sentForUser?.error || 'Kickback email was not sent.');
          }


          setUnmatchedRemediationByTimecampName((prev) => ({
            ...prev,
            [tc]: { ...prev?.[tc], resolved: true, busy: false, error: null },
          }));

          toaster.push(
            <Message showIcon type="success" closable>
              Kickback email sent to {recipientEmail} for {tc}.
            </Message>,
            { placement: 'topEnd', duration: 2600 }
          );
          return;
        }
      } catch (e) {
        setUnmatchedRemediationByTimecampName((prev) => ({
          ...prev,
          [tc]: { ...prev?.[tc], busy: false, error: e?.message || String(e) },
        }));

        toaster.push(
          <Message showIcon type="error" closable>
            Failed to remediate {tc}: {e?.message || String(e)}
          </Message>,
          { placement: 'topEnd', duration: 7000 }
        );
      }
    },
    [
      acceptUnmatchedUrl,
      authenticatedFetch,
      kickbackSendUrl,
      pullHoursUrlBase,
      refetchTaskActuals,
      unmatchedModalEntries,
      unmatchedModalMonth,
      unmatchedModalRows,
      unmatchedRemediationByTimecampName,
      userEmailById,
    ]
  );

  const unmatchedApplyAllState = useMemo(() => {
    const rows = Array.isArray(unmatchedModalRows) ? unmatchedModalRows : [];
    const canApplyTcs = [];
    const missing = [];
    const actionByTc = new Map();

    for (const r of rows) {
      const tc = String(r?.userName || '').trim();
      if (!tc) continue;
      const st = unmatchedRemediationByTimecampName?.[tc] || {};
      const action = st?.action ?? null;
      const mappedUserId = st?.userId ?? null;
      const resolved = Boolean(st?.resolved);
      const busy = Boolean(st?.busy);

      actionByTc.set(tc, action);

      const hasAction = Boolean(action);
      const needsUser = action === 'add' || action === 'unassigned';
      const hasUser = Number.isInteger(Number(mappedUserId));
      const isReady = hasAction && (!needsUser || hasUser);
      if (!isReady) missing.push(tc);

      const canApply = !resolved && !busy && isReady;
      if (canApply) canApplyTcs.push(tc);
    }

    const allReady = rows.length > 0 && missing.length === 0;
    return { allReady, canApplyTcs, actionByTc };
  }, [unmatchedModalRows, unmatchedRemediationByTimecampName]);

  const applyAllUnmatchedRemediations = useCallback(async () => {
    if (!unmatchedModalOpen) return;
    if (anyUnmatchedBusy) return;

    if (!unmatchedApplyAllState.allReady) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Assign an action (and app user when required) for each row.
        </Message>,
        { placement: 'topEnd', duration: 4200 }
      );
      return;
    }

    // Snapshot hours at click-time so “unassigned” can apply even if a later “add” pull refreshes the list.
    const hoursByTc = new Map(
      (Array.isArray(unmatchedModalRows) ? unmatchedModalRows : [])
        .map((r) => [String(r?.userName || '').trim(), Number(r?.hours ?? 0)])
        .filter(([tc]) => tc)
    );

    const priority = (action) => {
      if (action === 'unassigned') return 0;
      if (action === 'kickback') return 1;
      if (action === 'add') return 2;
      return 3;
    };

    const ordered = [...unmatchedApplyAllState.canApplyTcs].sort((a, b) => {
      const pa = priority(unmatchedApplyAllState.actionByTc.get(a));
      const pb = priority(unmatchedApplyAllState.actionByTc.get(b));
      if (pa !== pb) return pa - pb;
      return String(a).localeCompare(String(b));
    });

    for (const tc of ordered) {
      // applyRemediationForTimecampUser manages its own busy/resolved guards.
      // eslint-disable-next-line no-await-in-loop
      await applyRemediationForTimecampUser(tc, { hoursOverride: hoursByTc.get(tc) });
    }
  }, [anyUnmatchedBusy, applyRemediationForTimecampUser, unmatchedApplyAllState, unmatchedModalOpen, unmatchedModalRows]);

  const openMatchModalForTimecampUser = useCallback(
    (timecampUserName) => {
      const tc = String(timecampUserName || '').trim();
      if (!tc) return;
      const st = unmatchedRemediationByTimecampName?.[tc];
      const current = st && Number.isInteger(Number(st.userId)) ? Number(st.userId) : null;
      setMatchModalTimecampName(tc);
      setMatchModalUserId(current);
      setMatchModalOpen(true);
    },
    [unmatchedRemediationByTimecampName]
  );

  const saveMatchModal = useCallback(() => {
    const tc = String(matchModalTimecampName || '').trim();
    const uid = Number(matchModalUserId);
    if (!tc) return;
    if (!Number.isInteger(uid)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Select an app user to match.
        </Message>,
        { placement: 'topEnd', duration: 4000 }
      );
      return;
    }

    setUnmatchedRemediationByTimecampName((prev) => ({
      ...prev,
      [tc]: {
        ...prev?.[tc],
        userId: uid,
        error: null,
        autoMatched: false,
        autoMatchReason: 'manual',
      },
    }));

    setMatchModalOpen(false);
  }, [matchModalTimecampName, matchModalUserId]);

  const upsertTaskActualsUrl = useMemo(() => {
    const apiBase = resolveApiBaseUrl();
    return `${apiBase.replace(/\/+$/, '')}/tasks/${encodeURIComponent(String(taskId || ''))}/actuals`;
  }, [taskId]);

  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState(null);
  const [savingCell, setSavingCell] = useState(false);

  const editingCellRef = useRef(null);
  const editingValueRef = useRef(null);
  const inlineEditorRef = useRef(null);

  useEffect(() => {
    editingCellRef.current = editingCell;
  }, [editingCell]);

  useEffect(() => {
    editingValueRef.current = editingValue;
  }, [editingValue]);

  const beginEdit = useCallback(
    (userId, monthStart, currentHours) => {
      if (!allowManualEdit || !canFinance) return;
      if (savingCell) return;
      if (!Number.isInteger(Number(userId))) return;
      if (!isIsoDate(monthStart)) return;
      setEditingCell({ userId: Number(userId), monthStart });
      setEditingValue(Number.isFinite(Number(currentHours)) ? Number(currentHours) : 0);
    },
    [allowManualEdit, canFinance, savingCell]
  );

  const cancelEdit = useCallback(() => {
    setEditingCell(null);
    setEditingValue(null);
  }, []);

  const closeEditorIfSameCell = useCallback((cell) => {
    if (!cell) return;
    setEditingCell((prev) => {
      if (!prev) return prev;
      const same = Number(prev.userId) === Number(cell.userId) && String(prev.monthStart) === String(cell.monthStart);
      if (!same) return prev;
      setEditingValue(null);
      return null;
    });
  }, []);

  const saveEdit = useCallback(async (overrideCell = null, overrideValue = null) => {
    const cell = overrideCell ?? editingCell;
    const value = overrideValue ?? editingValue;
    if (!cell) return;
    if (!allowManualEdit) return;
    if (!Number.isInteger(taskId)) return;

    const userId = Number(cell.userId);
    const monthStart = cell.monthStart;
    const hours = Number(value);
    const normalized = Number.isFinite(hours) ? Math.max(0, hours) : 0;

    // If unchanged, just close the editor quietly.
    const current = Number(actualHoursByUserId.get(userId)?.[monthStart] ?? 0);
    if (Number.isFinite(current) && normalized === current) {
      closeEditorIfSameCell(cell);
      return;
    }

    setSavingCell(true);
    try {
      const resp = await authenticatedFetch(upsertTaskActualsUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monthStart, rows: [{ userId, hours: normalized }] }),
      });

      const json = await resp.json().catch(() => null);
      if (!resp.ok) {
        throw new Error(json?.error || `Failed to save hours (${resp.status})`);
      }

      toaster.push(
        <Message showIcon type="success" closable>
          Saved actual hours for {formatMonthLabel(monthStart)}.
        </Message>,
        { placement: 'topEnd', duration: 2400 }
      );

      if (typeof refetchTaskActuals === 'function') {
        await refetchTaskActuals();
      }

      closeEditorIfSameCell(cell);
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          Failed to save actual hours: {e?.message || String(e)}
        </Message>,
        { placement: 'topEnd', duration: 7000 }
      );
    } finally {
      setSavingCell(false);
    }
  }, [actualHoursByUserId, allowManualEdit, authenticatedFetch, closeEditorIfSameCell, editingCell, editingValue, refetchTaskActuals, taskId, upsertTaskActualsUrl]);

  // Save when clicking anywhere outside the inline editor.
  useEffect(() => {
    if (!allowManualEdit) return undefined;
    if (!editingCell) return undefined;

    const onPointerDownCapture = (e) => {
      const cell = editingCellRef.current;
      if (!cell) return;
      const root = inlineEditorRef.current;
      if (root && e?.target && root.contains(e.target)) return;

      // If the user clicks elsewhere, commit current value.
      const v = editingValueRef.current;
      void saveEdit({ ...cell }, v);
    };

    document.addEventListener('pointerdown', onPointerDownCapture, true);
    return () => document.removeEventListener('pointerdown', onPointerDownCapture, true);
  }, [allowManualEdit, editingCell, saveEdit]);

  const openClearModalForMonth = useCallback(
    (monthStart) => {
      if (!isIsoDate(monthStart)) return;
      setClearMonth(monthStart);
      setClearModalOpen(true);
    },
    []
  );

  return (
    <>
    <div className="ppc-noprint">
      <div ref={setToolbarEl} className="ppc-actuals-table-header ppc-actuals-table-header--sticky">
        <div className="ppc-actuals-table-header-left">
          <ProviderBadge provider={provider} isLoading={providerLoading} />
        </div>
        <div className="ppc-actuals-table-header-right">
          <div className="ppc-actuals-range-row">
            <IconButton
              appearance="subtle"
              size="sm"
              icon={<i className="bi bi-chevron-left" aria-hidden="true" />}
              aria-label="Previous month"
              title="Previous month"
              onClick={goPrevMonths}
            />
            <div className="ppc-actuals-range-label">{monthRangeLabel}</div>
            <IconButton
              appearance="subtle"
              size="sm"
              icon={<i className="bi bi-chevron-right" aria-hidden="true" />}
              aria-label="Next month"
              title="Next month"
              onClick={goNextMonths}
            />
            <Button appearance="subtle" size="sm" onClick={goCurrentMonths} disabled={isOnCurrentWindow}>
              Current
            </Button>

            {/* Grouped into one control to match the task order actuals view. The
                alerts here are scoped to this discipline only, so a lead can follow
                their own task without the rest of the task order. */}
            <div className="ppc-actuals-segment" role="group" aria-label="View actions">
              <button
                type="button"
                className="ppc-actuals-btn ppc-actuals-btn--sm"
                onClick={() => setPaceModalOpen(true)}
                disabled={!Number.isInteger(taskId)}
                title="How far through this month’s forecast each person is"
              >
                <i className="bi bi-speedometer2" aria-hidden="true" />
                View Pace
              </button>

              <button
                type="button"
                className="ppc-actuals-btn ppc-actuals-btn--sm"
                onClick={() => setAlertsModalOpen(true)}
                disabled={!Number.isInteger(projectId) || !Number.isInteger(taskOrderId) || !Number.isInteger(taskId)}
                title={
                  Number.isInteger(projectId) && Number.isInteger(taskOrderId)
                    ? 'Email alerts for this discipline'
                    : 'Open this page from a task order to set up email alerts'
                }
              >
                <i className="bi bi-clock" aria-hidden="true" />
                Email Alerts
              </button>

              <button
                type="button"
                className="ppc-actuals-btn ppc-actuals-btn--sm"
                onClick={handleExportXlsx}
                disabled={taskHourForecastLoading || taskActualsLoading}
              >
                <ExcelIcon />
                Export
              </button>

              <button
                type="button"
                className="ppc-actuals-btn ppc-actuals-btn--sm"
                onClick={() => setPrintMode(true)}
                disabled={printMode}
              >
                <i className="bi bi-printer" aria-hidden="true" />
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        ref={monthSlideContainerRef}
        className="ppc-actuals-grid-scroll"
        style={{ '--ppc-actuals-toolbar-h': `${toolbarH}px` }}
      >
      <div
        className="ppc-month-slide-grid"
        style={{
          minWidth: 820,
          display: 'grid',
          gridTemplateColumns: colTemplate,
          gap: 0,
          alignItems: 'stretch',
          border: '1px solid var(--rs-border-primary)',
          borderRadius: 10,
          overflow: 'clip',
        }}
      >
        <div
          className="ppc-month-slide-pin ppc-actuals-head-cell"
          style={{
            fontWeight: 900,
            padding: '10px 10px',
            borderBottom: '1px solid var(--rs-border-primary)',
            background: 'var(--rs-bg-well)',
          }}
        >
          Person
        </div>
        <MonthSlideCells
          months={months}
          prevMonth={prevMonth}
          nextMonth={nextMonth}
          renderCell={(m) => {
          const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
          const pullDisabled = !canPullHours || !isPullProviderActive || !canPull || pullingMonth !== null || isFutureMonth;
          const hasStoredFromApi = Array.isArray(taskActuals?.monthsWithRows)
            ? taskActuals.monthsWithRows.includes(m)
            : false;
          const localRefreshAt = refreshAttemptedAtByMonth?.[m] || null;
          const hasStored = hasStoredFromApi || Boolean(monthLastModifiedByMonth?.[m]) || Boolean(localRefreshAt);
          const refreshedAt = latestIsoTimestamp(monthLastModifiedByMonth?.[m], localRefreshAt);
          const refreshedDaysLabel = formatRefreshedLabel(refreshedAt);
          const pullLabel = hasStored ? 'Refresh hours' : 'Pull hours';
          const pullTooltip = isOdcTask
            ? `${providerLabel} pull is not applicable for ODC tasks`
            : !canPull
            ? `Link this task to ${providerLabel} to enable pulling hours`
            : isFutureMonth
              ? `Cannot pull ${providerLabel} hours for future months`
              : pullLabel;

          return (
            <div
              key={m}
              className="ppc-month-header"
              style={{
                padding: '10px 10px',
                borderBottom: '1px solid var(--rs-border-primary)',
                textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
                background: 'var(--rs-bg-well)',
              }}
            >
              <div className="ppc-month-title-row">
                <div className="ppc-month-title" style={{ fontWeight: 800 }}>{formatMonthLabel(m)}</div>
                <div className="ppc-month-actions">
                  <Whisper
                    placement="top"
                    speaker={<Tooltip>Clear saved actuals for this month…</Tooltip>}
                    trigger={['hover', 'focus']}
                  >
                    <IconButton
                      className="ppc-month-delete"
                      appearance="subtle"
                      size="xs"
                      icon={<i className="bi bi-trash" aria-hidden="true" />}
                      aria-label={`Clear actuals for ${formatMonthLabel(m)}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openClearModalForMonth(m);
                      }}
                      disabled={!Number.isInteger(taskId) || clearTaskActualsForMonth.isPending}
                    />
                  </Whisper>

                  {isPullProviderActive && !isOdcTask ? (
                    <Whisper
                      placement="top"
                      speaker={<Tooltip>{pullTooltip}</Tooltip>}
                      trigger={['hover', 'focus']}
                    >
                      <Button
                        className="ppc-month-refresh"
                        appearance="ghost"
                        size="xs"
                        loading={pullingMonth === m}
                        disabled={pullDisabled}
                        aria-label={pullLabel}
                        onClick={() => pullTimecampHoursForMonth(m)}
                      >
                        <i
                          className={hasStored ? 'bi bi-arrow-repeat' : 'bi bi-arrow-down'}
                          aria-hidden="true"
                        />
                      </Button>
                    </Whisper>
                  ) : null}
                </div>
              </div>
              {isPullProviderActive && !isOdcTask && refreshedDaysLabel ? (
                <div style={{ marginTop: 4, fontSize: 11, color: '#888', lineHeight: '14px' }}>
                  Refreshed {refreshedDaysLabel}
                </div>
              ) : null}
            </div>
          );
          }}
        />

        {people.map((p) => (
          <div key={p.userId ?? p.name} style={{ display: 'contents' }}>
            <div
              className="ppc-month-slide-pin"
              style={{
                padding: '10px 10px',
                borderBottom: '1px solid var(--rs-border-primary)',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={p.name}
            >
              {(() => {
                const timecardUpdatedLabel = formatDaysAgoLabel(p.timecardLastModifiedAt);
                return (
                  <>
              <Button
                appearance="link"
                size="xs"
                style={{
                  padding: 0,
                  fontWeight: 700,
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                }}
                title={taskForecastPath ? 'Go to task forecast' : undefined}
                disabled={!taskForecastPath}
                onClick={() => {
                  if (!taskForecastPath) return;
                  if (typeof onOpenTaskForecast === 'function') {
                    onOpenTaskForecast(taskForecastPath, {
                      fromActuals: true,
                      actualsPath: actualsReturnPath || null,
                    });
                  }
                }}
              >
                {p.name}
              </Button>
              {p.notAssigned ? (
                <div style={{ fontSize: 11, color: '#888', lineHeight: '14px' }}>Not assigned to task</div>
              ) : null}
              {timecardUpdatedLabel ? (
                <div style={{ fontSize: 11, color: '#888', lineHeight: '14px' }}>
                  Timecard updated {timecardUpdatedLabel}
                </div>
              ) : null}
                  </>
                );
              })()}
            </div>
            <MonthSlideCells
              months={months}
              prevMonth={prevMonth}
              nextMonth={nextMonth}
              renderCell={(m) => (
              <div
                key={`${p.userId ?? p.name}|${m}`}
                className={allowManualEdit && Number.isInteger(Number(p.userId)) ? 'ppc-actuals-cell-editable' : undefined}
                style={{
                  padding: '10px 10px',
                  borderBottom: '1px solid var(--rs-border-primary)',
                  textAlign: 'center',
                  fontVariantNumeric: 'tabular-nums',
                }}
                title={allowManualEdit && canFinance && Number.isInteger(Number(p.userId)) ? 'Click to edit actual hours' : undefined}
                onClick={() => {
                  if (!allowManualEdit) return;
                  const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
                  if (isFutureMonth) return;
                  const uid = Number(p.userId);
                  if (!Number.isInteger(uid)) return;
                  const act = Number(actualHoursByUserId.get(uid)?.[m] ?? 0);
                  beginEdit(uid, m, act);
                }}
              >
                {(() => {
                  const uid = Number(p.userId);
                  const hasUser = Number.isInteger(uid);
                  const proj = hasUser ? Number(forecastHoursByUserId.get(uid)?.[m] ?? 0) : null;
                  const act = hasUser ? Number(actualHoursByUserId.get(uid)?.[m] ?? 0) : null;
                  const showDelta = hasUser && !taskHourForecastLoading && !taskActualsLoading;
                  const delta = showDelta ? Number(act ?? 0) - Number(proj ?? 0) : null;
                  const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
                  const shortfallWarning =
                    showDelta &&
                    !isFutureMonth &&
                    shouldWarnProjectedShortfall({
                      monthStart: m,
                      projected: proj,
                      actual: act,
                      currentMonthStart,
                      ftePercent: userFteById.get(uid) ?? 100,
                      closeDates,
                    });

                  const isEditing =
                    allowManualEdit &&
                    hasUser &&
                    editingCell &&
                    Number(editingCell.userId) === uid &&
                    String(editingCell.monthStart) === String(m);

                  return (
                    <div>
                      <div
                        style={{
                          fontSize: 12,
                          color: '#666',
                          display: 'flex',
                          alignItems: 'baseline',
                          justifyContent: 'center',
                          gap: 12,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span>
                          <span style={{ fontWeight: 800, marginRight: 4 }}>Proj</span>
                          {hasUser ? (taskHourForecastLoading ? '…' : formatHours(proj)) : '—'}
                        </span>
                        {!isFutureMonth ? (
                          <span>
                            <span style={{ fontWeight: 800, marginRight: 4 }}>Act</span>
                            {hasUser ? (
                              taskActualsLoading ? (
                                '…'
                              ) : isEditing ? (
                                <span
                                  ref={inlineEditorRef}
                                  style={{ display: 'inline-flex', alignItems: 'center' }}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDownCapture={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      const cell = editingCellRef.current;
                                      if (!cell) return;
                                      void saveEdit({ ...cell }, editingValueRef.current);
                                    }
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      cancelEdit();
                                    }
                                  }}
                                >
                                  <InputNumber
                                    size="xs"
                                    min={0}
                                    step={0.25}
                                    value={editingValue}
                                    onChange={(v) => setEditingValue(v)}
                                    disabled={savingCell}
                                    style={{ width: 90 }}
                                  />
                                </span>
                              ) : (
                                <span className="ppc-actuals-act-value">
                                  {formatHours(act)}
                                  {allowManualEdit ? (
                                    <i className="bi bi-pencil" aria-hidden="true" style={{ fontSize: 12, color: '#888' }} />
                                  ) : null}
                                </span>
                              )
                            ) : (
                              '—'
                            )}
                          </span>
                        ) : null}
                      </div>
                      <div style={{ marginTop: 6, height: 1, background: 'var(--rs-border-primary)' }} />
                      <div style={{ marginTop: 6, height: 18, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {showDelta && !isFutureMonth ? (
                          <DeltaBadge delta={delta} projected={proj} actual={act} shortfallWarning={shortfallWarning} />
                        ) : null}
                      </div>
                    </div>
                  );
                })()}
              </div>
              )}
            />
          </div>
        ))}

        <div style={{ display: 'contents' }}>
          <div
            className="ppc-month-slide-pin"
            style={{
              padding: '10px 10px',
              borderTop: '2px solid var(--rs-border-primary)',
              fontWeight: 900,
              background: 'var(--rs-bg-well)',
            }}
          >
            Total
          </div>

          <MonthSlideCells
            months={months}
            prevMonth={prevMonth}
            nextMonth={nextMonth}
            renderCell={(m) => (
            <div
              key={`totals|${m}`}
              style={{
                padding: '10px 10px',
                borderTop: '2px solid var(--rs-border-primary)',
                textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
                background: 'var(--rs-bg-well)',
              }}
            >
              {(() => {
                const proj = Number(taskHourForecast?.totals?.[m] ?? 0);
                const act = Number(taskActuals?.totals?.[m] ?? 0);
                const showDelta = !taskHourForecastLoading && !taskActualsLoading;
                const delta = showDelta ? act - proj : null;
                const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);

                return (
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        color: '#444',
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'center',
                        gap: 12,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span>
                        <span style={{ fontWeight: 800, marginRight: 4 }}>Proj</span>
                        {taskHourForecastLoading ? '…' : formatHours(proj)}
                      </span>
                      {!isFutureMonth ? (
                        <span>
                          <span style={{ fontWeight: 800, marginRight: 4 }}>Act</span>
                          {taskActualsLoading ? '…' : formatHours(act)}
                        </span>
                      ) : null}
                    </div>
                    <div style={{ marginTop: 6, height: 1, background: 'var(--rs-border-primary)' }} />
                    <div style={{ marginTop: 6, height: 18, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {showDelta && !isFutureMonth ? <DeltaBadge delta={delta} projected={proj} actual={act} /> : null}
                    </div>
                  </div>
                );
              })()}
            </div>
            )}
          />
        </div>
      </div>
      </div>

      <ActualsEmailAlertsModal
        open={alertsModalOpen}
        onClose={() => setAlertsModalOpen(false)}
        projectId={Number.isInteger(projectId) ? projectId : null}
        taskOrderId={Number.isInteger(taskOrderId) ? taskOrderId : null}
        taskId={Number.isInteger(taskId) ? taskId : null}
        taskName={task?.task || null}
      />

      <ActualsPaceModal
        open={paceModalOpen}
        onClose={() => setPaceModalOpen(false)}
        projectId={Number.isInteger(projectId) ? projectId : Number(task?.projectId ?? task?.project_id) || null}
        taskId={Number.isInteger(taskId) ? taskId : null}
        scopeLabel={task?.task || ''}
      />

      <Modal
        open={exportModalOpen}
        onClose={() => {
          if (!exporting) setExportModalOpen(false);
        }}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Export actuals range</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', fontWeight: 700, marginBottom: 6 }}>From month</div>
              <SelectPicker
                block
                searchable
                cleanable={false}
                data={exportMonthOptions}
                value={exportStartMonth}
                onChange={(value) => {
                  const v = typeof value === 'string' ? value : '';
                  if (isIsoDate(v)) setExportStartMonth(v);
                }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', fontWeight: 700, marginBottom: 6 }}>To month</div>
              <SelectPicker
                block
                searchable
                cleanable={false}
                data={exportMonthOptions}
                value={exportEndMonth}
                onChange={(value) => {
                  const v = typeof value === 'string' ? value : '';
                  if (isIsoDate(v)) setExportEndMonth(v);
                }}
              />
            </div>

            {selectedExportMonths.length > 0 ? (
              <div style={{ fontSize: 12, color: '#666' }}>
                Exporting {selectedExportMonths.length} month{selectedExportMonths.length === 1 ? '' : 's'} ({selectedExportRangeLabel})
              </div>
            ) : (
              <Message showIcon type="warning">
                Choose a valid month range.
              </Message>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setExportModalOpen(false)} disabled={exporting}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            loading={exporting}
            disabled={selectedExportMonths.length === 0}
            onClick={handleExportConfirm}
          >
            Export
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={clearModalOpen} onClose={() => setClearModalOpen(false)} size="xs">
        <Modal.Header>
          <Modal.Title>Clear actuals</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            This will permanently delete saved actual hours for the selected month. This cannot be undone.
          </Message>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Month</div>
            <SelectPicker
              block
              searchable={false}
              cleanable={false}
              data={months.map((m) => ({ label: formatMonthLabel(m), value: m }))}
              value={clearMonth}
              onChange={(v) => setClearMonth(v)}
              placeholder="Select a month"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => setClearModalOpen(false)}
            disabled={clearTaskActualsForMonth.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={clearTaskActualsForMonth.isPending}
            disabled={!clearMonth}
            onClick={async () => {
              if (!clearMonth) return;
              await clearTaskActualsForMonth.mutateAsync({ taskId, monthStart: clearMonth });
              setClearModalOpen(false);
            }}
          >
            Clear
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={unmatchedModalOpen}
        onClose={() => {
          if (anyUnmatchedBusy) return;
          setUnmatchedModalOpen(false);
        }}
        size="md"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>Unmatched TimeCamp users</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            These users logged time in TimeCamp for this task in {formatMonthLabel(unmatchedModalMonth)}, but they aren’t
            currently assigned as a lead/supporter on this task (or their name didn’t match). Add them to the task roster
            to include their hours when pulling.
          </Message>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Details</div>
            <div style={{ border: '1px solid #e6e6e6', borderRadius: 8, overflow: 'hidden' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 110px',
                  gap: 0,
                  background: '#fafafa',
                  borderBottom: '1px solid #e6e6e6',
                  fontWeight: 800,
                  fontSize: 12,
                }}
              >
                <div style={{ padding: '8px 10px' }}>TimeCamp user</div>
                <div style={{ padding: '8px 10px', textAlign: 'right' }}>Hours</div>
              </div>

              {unmatchedModalRows.map((r) => (
                <div
                  key={r.userName}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 110px',
                    gap: 0,
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: 13,
                  }}
                >
                  <div style={{ padding: '8px 10px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.userName}>
                    {r.userName}
                  </div>
                  <div style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatHours(r.hours)}
                  </div>
                </div>
              ))}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 110px',
                  gap: 0,
                  background: '#fff',
                  fontWeight: 800,
                }}
              >
                <div style={{ padding: '8px 10px' }}>Total</div>
                <div style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {formatHours(totalUnmatchedHours)}
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <Message type="info" showIcon>
              Remediate each user independently. You can add someone to the task + accept, send a kickback email,
              or accept hours without adding them to the task roster.
            </Message>

            <div style={{ marginTop: 10, fontSize: 12, color: '#666' }}>
              Progress: {unmatchedProgress.resolved} / {unmatchedProgress.total} remediated
            </div>

            <div style={{ marginTop: 10, border: '1px solid #e6e6e6', borderRadius: 8, overflow: 'visible' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '260px 1fr 120px',
                  gap: 0,
                  background: '#fafafa',
                  borderBottom: '1px solid #e6e6e6',
                  fontWeight: 800,
                  fontSize: 12,
                }}
              >
                <div style={{ padding: '8px 10px' }}>Action</div>
                <div style={{ padding: '8px 10px' }}>App user</div>
                <div style={{ padding: '8px 10px' }} />
              </div>

              {unmatchedModalRows.map((r) => {
                const tc = String(r?.userName || '').trim();
                const st = unmatchedRemediationByTimecampName?.[tc] || {};
                const action = st?.action ?? null;
                const mappedUserId = st?.userId ?? null;
                const mappedUserName = Number.isInteger(Number(mappedUserId)) ? userNameById.get(Number(mappedUserId)) : null;
                const mappedUserEmail = Number.isInteger(Number(mappedUserId)) ? userEmailById.get(Number(mappedUserId)) : '';
                const resolved = Boolean(st?.resolved);
                const busy = Boolean(st?.busy);
                const error = st?.error ? String(st.error) : '';

                const userNotFound = !Number.isInteger(Number(mappedUserId));

                const hasMappedUser = Number.isInteger(Number(mappedUserId));
                const hasMappedEmail = Boolean(mappedUserEmail);
                const canApply =
                  !resolved &&
                  !busy &&
                  action &&
                  ((action === 'add' || action === 'unassigned') ? hasMappedUser : true) &&
                  (action === 'kickback' ? hasMappedUser && hasMappedEmail : true);

                return (
                  <div
                    key={`remediate:${tc}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '260px 1fr 120px',
                      gap: 0,
                      borderBottom: '1px solid #f0f0f0',
                      fontSize: 13,
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ padding: '8px 10px' }}>
                      <Whisper
                        placement="bottomStart"
                        trigger="click"
                        speaker={renderUnmatchedActionMenuFor(tc)}
                        container={() => document.body}
                      >
                        <Button
                          size="sm"
                          appearance="default"
                          disabled={busy || resolved || anyUnmatchedBusy}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                          }}
                        >
                          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {getUnmatchedActionLabel(action)}
                          </span>
                          <i className="bi bi-caret-down-fill" aria-hidden="true" style={{ fontSize: 12, color: '#888' }} />
                        </Button>
                      </Whisper>
                    </div>
                    <div style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          {userNotFound ? (
                            <Whisper
                              placement="top"
                              speaker={<Tooltip>This user was not found in the system (click to fix)</Tooltip>}
                              trigger={['hover', 'focus']}
                            >
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={() => openMatchModalForTimecampUser(tc)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openMatchModalForTimecampUser(tc);
                                  }
                                }}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  cursor: busy || resolved || anyUnmatchedBusy ? 'not-allowed' : 'pointer',
                                  color: '#b00020',
                                  userSelect: 'none',
                                  opacity: busy || resolved || anyUnmatchedBusy ? 0.6 : 1,
                                }}
                                aria-disabled={busy || resolved || anyUnmatchedBusy}
                              >
                                <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                                <span style={{ fontSize: 12 }}>Not found</span>
                              </span>
                            </Whisper>
                          ) : (
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => openMatchModalForTimecampUser(tc)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openMatchModalForTimecampUser(tc);
                                }
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                cursor: busy || resolved || anyUnmatchedBusy ? 'not-allowed' : 'pointer',
                                opacity: busy || resolved || anyUnmatchedBusy ? 0.6 : 1,
                                minWidth: 0,
                              }}
                              aria-disabled={busy || resolved || anyUnmatchedBusy}
                              title="Click to change match"
                            >
                              <span
                                style={{
                                  fontWeight: 700,
                                  fontSize: 12,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {action === 'kickback'
                                  ? (mappedUserEmail || mappedUserName || `User ${mappedUserId}`)
                                  : (mappedUserName || `User ${mappedUserId}`)}
                              </span>
                              <i className="bi bi-pencil" aria-hidden="true" style={{ fontSize: 12, color: '#888' }} />
                            </div>
                          )}

                          {resolved ? <span style={{ fontSize: 11, color: '#2e7d32', fontWeight: 700 }}>Done</span> : null}
                        </div>

                        <div
                          style={{
                            fontSize: 11,
                            color: '#666',
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={tc}
                        >
                          TimeCamp: {tc}
                        </div>

                        {st?.autoMatched && st?.autoMatchReason ? (
                          <div style={{ fontSize: 11, color: '#666' }}>Auto-matched by name</div>
                        ) : null}
                        {error ? <div style={{ fontSize: 11, color: '#b00020' }}>{error}</div> : null}
                      </div>
                    </div>
                    <div style={{ padding: '8px 10px' }}>
                      <Button
                        size="xs"
                        appearance={resolved ? 'subtle' : 'primary'}
                        disabled={!canApply || anyUnmatchedBusy}
                        loading={busy}
                        onClick={() => applyRemediationForTimecampUser(tc)}
                      >
                        {resolved ? 'Done' : 'Apply'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton disabled={anyUnmatchedBusy} onClick={() => setUnmatchedModalOpen(false)}>
            Close
          </NsiButton>
          {unmatchedApplyAllState.allReady && !allUnmatchedResolved ? (
            canFinance ? (
              <NsiButton variant="primary" disabled={anyUnmatchedBusy || unmatchedApplyAllState.canApplyTcs.length === 0} onClick={applyAllUnmatchedRemediations}>
                Apply all
              </NsiButton>
            ) : null
          ) : (
            <NsiButton variant="primary" disabled={!allUnmatchedResolved || anyUnmatchedBusy} onClick={() => setUnmatchedModalOpen(false)}>
              Done
            </NsiButton>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        open={matchModalOpen}
        onClose={() => setMatchModalOpen(false)}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Match TimeCamp user</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>TimeCamp user</div>
          <div style={{ fontWeight: 800, marginBottom: 12 }}>{matchModalTimecampName}</div>

          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Match to app user</div>
          <SelectPicker
            block
            data={userOptions}
            value={matchModalUserId}
            onChange={(v) => setMatchModalUserId(v)}
            placeholder="Select user"
            searchable
            cleanable
          />
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setMatchModalOpen(false)}>
            Cancel
          </NsiButton>
          <NsiButton variant="primary" onClick={saveMatchModal}>
            Save
          </NsiButton>
        </Modal.Footer>
      </Modal>

      {!unmatchedModalOpen && unmatchedProgress.total > 0 && unmatchedProgress.resolved < unmatchedProgress.total ? (
        <div style={{ marginTop: 10 }}>
          <Message type="warning" showIcon>
            Unmatched TimeCamp users still need remediation for {formatMonthLabel(unmatchedModalMonth)} ({
              unmatchedProgress.resolved
            }/{unmatchedProgress.total}).
            <div style={{ marginTop: 8 }}>
              {canFinance ? (
                <Button appearance="ghost" size="xs" onClick={() => setUnmatchedModalOpen(true)}>
                  Review / Remediate
                </Button>
              ) : null}
              {!canFinance ? (
                <span style={{ marginLeft: 8, fontSize: 12 }}>Remediating needs Manage Finance on this project.</span>
              ) : null}
            </div>
          </Message>
        </div>
      ) : null}

      {isPullProviderActive && !isOdcTask && !canPull ? (
        <div style={{ marginTop: 10 }}>
          <Message type="info" showIcon>
            Link this task to {providerLabel} to enable pulling hours.
          </Message>
        </div>
      ) : null}

      {taskHourForecastError ? (
        <div style={{ marginTop: 10 }}>
          <Message type="error" showIcon>
            Failed to load projected hours.
          </Message>
        </div>
      ) : null}

      {taskActualsError ? (
        <div style={{ marginTop: 10 }}>
          <Message type="error" showIcon>
            Failed to load actual hours.
          </Message>
        </div>
      ) : null}
    </div>

    <div className="ppc-print-area" aria-hidden={!printMode}>
      <div className="ppc-print-header">
        <div className="ppc-print-header-row">
          <div className="ppc-print-title">Actuals</div>
          <div className="ppc-print-meta">
            <div className="ppc-print-meta-item">
              Task: <span style={{ fontWeight: 700 }}>{task?.task || task?.name || `Task ${taskId}`}</span>
            </div>
            <div className="ppc-print-meta-item">
              Range: <span style={{ fontWeight: 700 }}>{monthRangeLabel}</span>
            </div>
          </div>
        </div>
      </div>

      <table className="ppc-print-table">
        <thead>
          <tr>
            <th rowSpan={2} style={{ textAlign: 'left' }}>Person</th>
            {months.map((m) => (
              <th key={`mh:${m}`} colSpan={2}>{formatMonthLabel(m)}</th>
            ))}
          </tr>
          <tr>
            {months.flatMap((m) => [
              <th key={`subp:${m}`}>Proj</th>,
              <th key={`suba:${m}`}>Act</th>,
            ])}
          </tr>
        </thead>
        <tbody>
          {people.map((p) => {
            const uid = Number(p?.userId);
            const hasUser = Number.isInteger(uid);
            const label = `${String(p?.name || '—')}${p?.notAssigned ? ' (Not assigned)' : ''}`;

            return (
              <tr key={`p:${String(p?.userId ?? p?.name)}`}> 
                <td style={{ minWidth: 220 }}>{label}</td>
                {months.map((m) => {
                  const proj = hasUser ? Number(forecastHoursByUserId.get(uid)?.[m] ?? 0) : 0;
                  const act = hasUser ? Number(actualHoursByUserId.get(uid)?.[m] ?? 0) : 0;
                  return (
                    [
                      <td key={`pp:${uid}|${m}`} className="ppc-num">{Number(proj).toFixed(2)}</td>,
                      <td key={`pa:${uid}|${m}`} className="ppc-num">{Number(act).toFixed(2)}</td>,
                    ]
                  );
                })}
              </tr>
            );
          })}

          <tr className="ppc-print-total-row">
            <td>Total</td>
            {months.flatMap((m) => [
              <td key={`tp:${m}`} className="ppc-num">{Number(taskHourForecast?.totals?.[m] ?? 0).toFixed(2)}</td>,
              <td key={`ta:${m}`} className="ppc-num">{Number(taskActuals?.totals?.[m] ?? 0).toFixed(2)}</td>,
            ])}
          </tr>
        </tbody>
      </table>
    </div>
    </>
  );
}

export default function TaskActualsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const taskId = Number(params?.taskId);
  const projectIdParam = Number(params?.projectId);
  const taskOrderIdParam = Number(params?.taskOrderId);

  const autoPullMonthStart = useMemo(() => {
    const sp = new URLSearchParams(location?.search || '');
    const v = sp.get('pullMonthStart') || sp.get('monthStart') || '';
    const iso = String(v).slice(0, 10);
    return isIsoDate(iso) ? iso : null;
  }, [location?.search]);

  const { data: activeProvider, isLoading: providerLoading } = useActiveProvider();
  const activeProviderName = activeProvider?.name || null;
  const isTimeCampActive = activeProviderName === 'timecamp';
  // Providers that link a task to an external project (+ optional task), e.g.
  // Clockify and EmployeeConnect. Declared by the provider, not named here.
  const isProjectLinkProviderActive = activeProvider?.capabilities?.linkShape === 'project-task';
  const activeProviderLabel = activeProvider?.displayName || activeProviderName || 'provider';
  const isManualActive = activeProviderName === 'manual';

  const showProviderConfigCta = !providerLoading && (!activeProviderName || activeProvider?.isConfigured === false);

  const queryClient = useQueryClient();

  // Project-task links live in task_provider_links rather than on the task row, so
  // saving one has to invalidate the task and actuals queries explicitly.
  const handleProviderLinkSaved = useCallback(
    (payload) => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths'] });

      toaster.push(
        <Message showIcon type="success" closable>
          {payload?.isLinked ? `${activeProviderLabel} link saved.` : `${activeProviderLabel} link removed.`}
        </Message>,
        { placement: 'topEnd', duration: 2400 }
      );
    },
    [activeProviderLabel, queryClient, taskId]
  );

  const authenticatedFetch = useAuthenticatedFetch();
  const [apiModalOpen, setApiModalOpen] = useState(false);
  const [timecampLoading, setTimecampLoading] = useState(false);
  const [timecampError, setTimecampError] = useState(null);
  const [timecampTasksTree, setTimecampTasksTree] = useState([]);
  const [timecampSelectedTaskId, setTimecampSelectedTaskId] = useState(null);
  const [timecampTreeIndex, setTimecampTreeIndex] = useState({ nodesById: new Map(), parentById: new Map() });
  const [timecampSaveLoading, setTimecampSaveLoading] = useState(false);
  const [timecampSaveError, setTimecampSaveError] = useState(null);
  const [timecampLastSavedTaskId, setTimecampLastSavedTaskId] = useState(null);

  const [linkedTimecampTaskId, setLinkedTimecampTaskId] = useState(null);
  const [linkedTimecampTaskName, setLinkedTimecampTaskName] = useState(null);

  const timecampLastRequestedTaskIdRef = useRef(null);
  const timecampSaveInFlightRef = useRef(false);

  const { data: task, isLoading, error } = useTask(Number.isInteger(taskId) ? taskId : null);
  const isOdcTask = String(task?.taskType || '').trim().toLowerCase() === 'odc';

  const serverLinkedTimecampTaskId = useMemo(() => {
    const raw = task?.timecampTaskId;
    if (raw === null || raw === undefined || raw === '') return null;
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  }, [task?.timecampTaskId]);

  const serverLinkedTimecampTaskName = useMemo(() => {
    const s = task?.timecampTaskName;
    const name = typeof s === 'string' ? s.trim() : '';
    return name ? name : null;
  }, [task?.timecampTaskName]);

  const isTaskLinkWarningIgnored = task?.ignoreTimecampLinkWarning === true;

  useEffect(() => {
    setLinkedTimecampTaskId(serverLinkedTimecampTaskId);
    setTimecampLastSavedTaskId(serverLinkedTimecampTaskId);
    if (!Number.isInteger(serverLinkedTimecampTaskId)) {
      setLinkedTimecampTaskName(null);
    } else {
      setLinkedTimecampTaskName(serverLinkedTimecampTaskName);
    }
  }, [serverLinkedTimecampTaskId, serverLinkedTimecampTaskName]);

  const updateTask = useUpdateTask({
    onSuccess: (_data, variables) => {
      const payload = variables?.payload || {};
      const updatedMapping = payload?.timecampTaskID !== undefined || payload?.timecampTaskId !== undefined || payload?.timecamp_task_id !== undefined;
      const updatedIgnore = payload?.ignoreTimecampLinkWarning !== undefined || payload?.ignore_timecamp_link_warning !== undefined;

      toaster.push(
        <Message showIcon type="success" closable>
          {updatedMapping
            ? 'TimeCamp task mapping saved.'
            : updatedIgnore
              ? 'Task-link warning setting updated.'
              : 'Task updated.'}
        </Message>,
        { placement: 'topEnd', duration: 2400 }
      );
    },
  });

  const timecampTasksUrl = useMemo(() => {
    const apiBase = resolveApiBaseUrl();
    return `${apiBase.replace(/\/+$/, '')}/providers/timecamp/tasks?minimal=true`;
  }, []);

  const handleFetchTimecampTasks = useCallback(async () => {
    setTimecampLoading(true);
    setTimecampError(null);
    setTimecampTasksTree([]);
    setTimecampTreeIndex({ nodesById: new Map(), parentById: new Map() });
    try {
      const resp = await authenticatedFetch(timecampTasksUrl, { method: 'GET' });

      const text = await resp.text();
      const parsed = tryParseJsonText(text);

      if (parsed && typeof parsed === 'object') {
        const timecampPayload =
          parsed?.json && typeof parsed.json === 'object' ? parsed.json : parsed;
        const { tasks, error: jsonErr } = parseTimecampTasksJson(timecampPayload);
        if (jsonErr) {
          setTimecampError(jsonErr);
          return;
        }
        const tree = buildTimecampTaskTree(tasks);
        setTimecampTasksTree(tree.roots);
        setTimecampTreeIndex({ nodesById: tree.nodesById, parentById: tree.parentById });
        return;
      }

      // Fallback: raw response (likely HTML error page)
      if (isProbablyHtml(text)) {
        setTimecampError(
          `Received HTML instead of JSON. This usually means the API base URL is wrong (proxy not hit) or you got an auth/redirect page. URL: ${timecampTasksUrl}`
        );
        return;
      }

      const direct = tryParseJsonText(text);
      if (direct && typeof direct === 'object') {
        const { tasks, error: jsonErr } = parseTimecampTasksJson(direct);
        if (jsonErr) {
          setTimecampError(jsonErr);
          return;
        }
        const tree = buildTimecampTaskTree(tasks);
        setTimecampTasksTree(tree.roots);
        setTimecampTreeIndex({ nodesById: tree.nodesById, parentById: tree.parentById });
        return;
      }

      setTimecampError('Unexpected response format (not JSON or HTML).');
    } catch (e) {
      setTimecampError(e?.message || String(e));
    } finally {
      setTimecampLoading(false);
    }
  }, [authenticatedFetch, timecampTasksUrl]);

  useEffect(() => {
    if (!apiModalOpen) return;
    // Auto-load tasks when opening the modal.
    handleFetchTimecampTasks();
  }, [apiModalOpen, handleFetchTimecampTasks]);

  useEffect(() => {
    if (!apiModalOpen) return;
    if (timecampSelectedTaskId !== null && timecampSelectedTaskId !== undefined) return;
    if (!Number.isInteger(linkedTimecampTaskId)) return;
    setTimecampSelectedTaskId(linkedTimecampTaskId);
  }, [apiModalOpen, linkedTimecampTaskId, timecampSelectedTaskId]);

  const selectedTimecampNode = useMemo(() => {
    const id = Number(timecampSelectedTaskId);
    if (!Number.isInteger(id)) return null;
    return timecampTreeIndex?.nodesById?.get?.(id) || null;
  }, [timecampSelectedTaskId, timecampTreeIndex]);

  const selectedTimecampPath = useMemo(() => {
    if (!Number.isInteger(Number(timecampSelectedTaskId))) return '';
    const labels = buildTimecampPathLabels(
      timecampSelectedTaskId,
      timecampTreeIndex.nodesById,
      timecampTreeIndex.parentById
    );
    return labels.join(' / ');
  }, [timecampSelectedTaskId, timecampTreeIndex]);

  const canSaveTimecampSelection = useMemo(() => {
    if (!apiModalOpen) return false;
    if (!Number.isInteger(taskId)) return false;
    const id = Number(timecampSelectedTaskId);
    if (!Number.isInteger(id)) return false;
    if (Number(timecampLastSavedTaskId) === id) return false;
    return true;
  }, [apiModalOpen, taskId, timecampLastSavedTaskId, timecampSelectedTaskId]);

  const saveTimecampSelection = async (id) => {
    if (!apiModalOpen) return;
    if (!Number.isInteger(taskId)) return;
    if (!Number.isInteger(id)) return;

    if (Number(timecampLastSavedTaskId) === Number(id)) return;
    if (Number(timecampLastRequestedTaskIdRef.current) === Number(id)) return;
    if (timecampSaveInFlightRef.current) return;

    timecampLastRequestedTaskIdRef.current = id;
    timecampSaveInFlightRef.current = true;
    setTimecampSaveError(null);
    setTimecampSaveLoading(true);

    try {
      await updateTask.mutateAsync({
        taskId,
        projectId: Number.isInteger(Number(task?.projectId)) ? Number(task.projectId) : undefined,
        payload: {
          timecampTaskID: id,
          timecampTaskName: selectedTimecampNode?.label ?? null,
          ignoreTimecampLinkWarning: false,
        },
      });
      setTimecampLastSavedTaskId(id);
      setLinkedTimecampTaskId(id);
      if (selectedTimecampNode?.label) {
        setLinkedTimecampTaskName(String(selectedTimecampNode.label));
      } else {
        setLinkedTimecampTaskName(null);
      }
    } catch (e) {
      setTimecampSaveError(e?.message || String(e));
    } finally {
      timecampSaveInFlightRef.current = false;
      setTimecampSaveLoading(false);
    }
  };

  const projectId = Number.isInteger(projectIdParam)
    ? projectIdParam
    : (Number.isInteger(Number(task?.projectId)) ? Number(task.projectId) : null);
  // Linking the discipline to its hours source is part of the discipline: Manage Disciplines.
  const canManageDisciplines = usePermissions().canProject(projectId, 'disciplines');
  const taskOrderId = Number.isInteger(taskOrderIdParam)
    ? taskOrderIdParam
    : (Number.isInteger(Number(task?.taskOrderId)) ? Number(task.taskOrderId) : null);

  const { data: taskOrderTasksRes = [] } = useTasks(
    { projectId, taskOrderId },
    { enabled: Number.isInteger(projectId) && Number.isInteger(taskOrderId) }
  );

  const { data: taskOrdersData = [] } = useTaskOrders(Number.isInteger(projectId) ? projectId : null, {
    enabled: Number.isInteger(projectId),
  });

  const selectedTaskOrder = useMemo(() => {
    if (!Number.isInteger(taskOrderId)) return null;
    const rows = Array.isArray(taskOrdersData) ? taskOrdersData : [];
    return rows.find((row) => Number(row?.id) === Number(taskOrderId)) || null;
  }, [taskOrderId, taskOrdersData]);

  const taskOrderTaskPickerData = useMemo(() => {
    const rows = Array.isArray(taskOrderTasksRes) ? taskOrderTasksRes : [];
    const taskRows = rows
      .map((t) => {
        const id = Number(t?.id);
        if (!Number.isInteger(id)) return null;
        const label = String(t?.task || '').trim() || `Task ${id}`;
        return { value: id, label };
      })
      .filter((x) => !!x)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));

    if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
      return [{ value: '__full_task_order__', label: 'Full Task Order' }, ...taskRows];
    }

    return taskRows;
  }, [projectId, taskOrderId, taskOrderTasksRes]);

  const showTaskOrderTaskSwitcher = Number.isInteger(projectId) && Number.isInteger(taskOrderId) && taskOrderTaskPickerData.length > 0;

  const taskOrderPath =
    Number.isInteger(projectId) && Number.isInteger(taskOrderId)
      ? `/projects/${projectId}/task-orders/${taskOrderId}`
      : null;

  const firstActivityId = Number(Array.isArray(task?.activities) ? task.activities?.[0]?.id : null);
  const taskForecastPath = Number.isInteger(taskId) && Number.isInteger(firstActivityId)
    ? (Number.isInteger(projectId) && Number.isInteger(taskOrderId)
      ? `/projects/${projectId}/task-orders/${taskOrderId}/tasks/${taskId}/activities/${firstActivityId}/forecast`
      : `/tasks/${taskId}/activities/${firstActivityId}/forecast`)
    : null;

  const actualsReturnPath = `${location?.pathname || ''}${location?.search || ''}`;

  return (
    <div style={{ width: '90%', maxWidth: 1600, margin: '0 auto' }}>
      <div className="ppc-actuals-shell">
      <div className="ppc-actuals-shell__head">
      <Stack justifyContent="space-between" alignItems="center" wrap spacing={10}>
        <Stack spacing={10} alignItems="center" wrap>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <div className="ppc-actuals-crumbs">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="ppc-actuals-crumb-link"
            >
              Projects
            </button>

            <i className="bi bi-chevron-right ppc-actuals-crumb-sep" aria-hidden="true" />

            <button
              type="button"
              onClick={() => {
                if (Number.isInteger(projectId)) navigate(`/projects/${projectId}`);
              }}
              disabled={!Number.isInteger(projectId)}
              className="ppc-actuals-crumb-link"
            >
              {task?.projectName
                ? task.projectName
                : Number.isInteger(projectId)
                  ? `Project ${projectId}`
                  : 'Project'}
            </button>

            {Number.isInteger(taskOrderId) ? (
              <>
                <i className="bi bi-chevron-right ppc-actuals-crumb-sep" aria-hidden="true" />
                <button
                  type="button"
                  onClick={() => {
                    if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
                      navigate(`/projects/${projectId}/task-orders/${taskOrderId}`);
                    }
                  }}
                  disabled={!Number.isInteger(projectId) || !Number.isInteger(taskOrderId)}
                  className="ppc-actuals-crumb-link"
                >
                  {selectedTaskOrder?.name || `Task order ${taskOrderId}`}
                </button>
              </>
            ) : null}

            <i className="bi bi-chevron-right ppc-actuals-crumb-sep" aria-hidden="true" />
            <button
              type="button"
              onClick={() => {
                if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
                  navigate(`/projects/${projectId}/task-orders/${taskOrderId}/actuals`, {
                    state: {
                      fromTaskActuals: true,
                      taskActualsPath: actualsReturnPath || null,
                    },
                  });
                  return;
                }
                if (Number.isInteger(projectId)) {
                  navigate(`/projects/${projectId}/actuals`, {
                    state: {
                      fromTaskActuals: true,
                      taskActualsPath: actualsReturnPath || null,
                    },
                  });
                }
              }}
              disabled={!Number.isInteger(projectId)}
              className="ppc-actuals-crumb-link"
            >
              Actuals
            </button>

            <i className="bi bi-chevron-right ppc-actuals-crumb-sep" aria-hidden="true" />
            <span className="ppc-actuals-crumb-current">
              {task?.task || (Number.isInteger(taskId) ? `Task ${taskId}` : 'Discipline')}
            </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, lineHeight: '22px' }}>
                <span className="ppc-actuals-title">
                  <i className="bi bi-clipboard-data ppc-actuals-title-icon" aria-hidden="true" />
                  <span>Actuals</span>
                </span>
              </h3>
            </div>
          </div>
        </Stack>

        <Stack spacing={10} wrap>
          {showTaskOrderTaskSwitcher ? (
            <div style={{ minWidth: 320, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 13, color: '#666', fontWeight: 700 }}>Discipline</div>
              <div style={{ minWidth: 260, flex: 1 }}>
                <SelectPicker
                  block
                  searchable
                  cleanable={false}
                  size="sm"
                  placeholder="Switch task"
                  data={taskOrderTaskPickerData}
                  value={Number.isInteger(taskId) ? taskId : null}
                  onChange={(value) => {
                    if (value === '__full_task_order__') {
                      if (!Number.isInteger(projectId) || !Number.isInteger(taskOrderId)) return;
                      const search = location?.search || '';
                      navigate(`/projects/${projectId}/task-orders/${taskOrderId}/actuals${search}`, {
                        state: {
                          fromTaskActuals: true,
                          taskActualsPath: actualsReturnPath || null,
                        },
                      });
                      return;
                    }

                    const nextTaskId = Number(value);
                    if (!Number.isInteger(nextTaskId)) return;
                    if (!Number.isInteger(projectId) || !Number.isInteger(taskOrderId)) return;
                    const search = location?.search || '';
                    navigate(`/projects/${projectId}/task-orders/${taskOrderId}/tasks/${nextTaskId}/actuals${search}`);
                  }}
                />
              </div>
            </div>
          ) : null}
          <button
            type="button"
            className="ppc-actuals-btn"
            onClick={() => {
              if (taskOrderPath) {
                navigate(taskOrderPath);
                return;
              }
              if (Number.isInteger(taskId)) navigate(`/tasks/${taskId}`);
              else navigate('/');
            }}
          >
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Back
          </button>
          {isManualActive ? (
            <button
              type="button"
              className="ppc-actuals-btn"
              onClick={() => {
                const qs = new URLSearchParams();
                if (Number.isInteger(projectId)) qs.set('projectId', String(projectId));
                if (Number.isInteger(taskOrderId)) qs.set('taskOrderId', String(taskOrderId));
                const search = qs.toString();
                navigate(`/import-hours${search ? `?${search}` : ''}`);
              }}
            >
              <i className="bi bi-download" aria-hidden="true" />
              Import from Sheet
            </button>
          ) : null}
        </Stack>
      </Stack>
      </div>

      <Divider style={{ margin: '8px 0 12px' }} />
      </div>

      {showProviderConfigCta ? (
        <div style={{ marginBottom: 12 }}>
          <Message type="warning" showIcon closable>
            You have not configured the source for your actual hours.{' '}
            <Button appearance="link" size="xs" onClick={() => navigate('/configuration')}>
              Click here to configure
            </Button>
          </Message>
        </div>
      ) : null}

      {isTimeCampActive ? (
        Number.isInteger(linkedTimecampTaskId) && linkedTimecampTaskId > 0 ? (
          <div style={{ marginBottom: 12 }}>
            <Message type="success" showIcon>
              <Stack justifyContent="space-between" alignItems="center" wrap spacing={10}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800 }}>TimeCamp task successfully linked</div>
                  <div style={{ fontSize: 13, color: '#2e2e2e', marginTop: 4, minWidth: 0 }}>
                    {linkedTimecampTaskName ? (
                      <span style={{ fontWeight: 700 }}>{linkedTimecampTaskName}</span>
                    ) : (
                      <span style={{ fontWeight: 700 }}>ID: {linkedTimecampTaskId}</span>
                    )}
                  </div>
                </div>
                <Stack spacing={8}>
                  {isTaskLinkWarningIgnored ? (
                    canManageDisciplines ? (
                      <Button
                        appearance="subtle"
                        size="sm"
                        loading={updateTask.isPending}
                        onClick={async () => {
                          await updateTask.mutateAsync({
                            taskId,
                            projectId: Number.isInteger(Number(task?.projectId)) ? Number(task.projectId) : undefined,
                            payload: { ignoreTimecampLinkWarning: false },
                          });
                        }}
                      >
                        Stop ignoring
                      </Button>
                    ) : null
                  ) : null}
                  {canManageDisciplines ? (
                    <Button appearance="ghost" size="sm" onClick={() => setApiModalOpen(true)}>
                      Edit
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </Message>
          </div>
        ) : (
          <div style={{ marginBottom: 12 }}>
            <Message type="warning" showIcon>
              <Stack justifyContent="space-between" alignItems="center" wrap spacing={10}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800 }}>No TimeCamp task linked for this task</div>
                  {isOdcTask ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                      Optional for ODC tasks.
                    </div>
                  ) : null}
                  {isTaskLinkWarningIgnored ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#0958d9', fontWeight: 700 }}>
                      Link warning is currently ignored for this task.
                    </div>
                  ) : null}
                </div>
                <Stack spacing={8}>
                  {isTaskLinkWarningIgnored ? (
                    canManageDisciplines ? (
                      <Button
                        appearance="subtle"
                        size="sm"
                        loading={updateTask.isPending}
                        onClick={async () => {
                          await updateTask.mutateAsync({
                            taskId,
                            projectId: Number.isInteger(Number(task?.projectId)) ? Number(task.projectId) : undefined,
                            payload: { ignoreTimecampLinkWarning: false },
                          });
                        }}
                      >
                        Stop ignoring
                      </Button>
                    ) : null
                  ) : null}
                  {canManageDisciplines ? (
                    <Button appearance="primary" size="sm" onClick={() => setApiModalOpen(true)}>
                      Select Task
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </Message>
          </div>
        )
      ) : null}

      {isProjectLinkProviderActive ? (
        task?.isLinkedToActiveProvider ? (
          <div style={{ marginBottom: 12 }}>
            <Message type="success" showIcon>
              <Stack justifyContent="space-between" alignItems="center" wrap spacing={10}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800 }}>{activeProviderLabel} project successfully linked</div>
                  <div style={{ fontSize: 13, color: '#2e2e2e', marginTop: 4, minWidth: 0 }}>
                    <span style={{ fontWeight: 700 }}>
                      {task?.providerLink?.externalProjectName || task?.providerLink?.externalProjectRef}
                      {task?.providerLink?.externalTaskName ? ` › ${task.providerLink.externalTaskName}` : ''}
                    </span>
                    {!task?.providerLink?.externalTaskRef ? (
                      <span style={{ color: '#8c6d1f', marginLeft: 8 }}>(entire project)</span>
                    ) : null}
                  </div>
                </div>
                {canManageDisciplines ? (
                  <Button appearance="ghost" size="sm" onClick={() => setApiModalOpen(true)}>
                    Edit
                  </Button>
                ) : null}
              </Stack>
            </Message>
          </div>
        ) : (
          <div style={{ marginBottom: 12 }}>
            <Message type="warning" showIcon>
              <Stack justifyContent="space-between" alignItems="center" wrap spacing={10}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800 }}>No {activeProviderLabel} project linked for this task</div>
                  {isOdcTask ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>Optional for ODC tasks.</div>
                  ) : null}
                </div>
                {canManageDisciplines ? (
                  <Button appearance="primary" size="sm" onClick={() => setApiModalOpen(true)}>
                    Select Project
                  </Button>
                ) : null}
              </Stack>
            </Message>
          </div>
        )
      ) : null}

      {error ? <Message type="error" showIcon>Failed to load task.</Message> : null}
      {!Number.isInteger(taskId) ? <Message type="error" showIcon>Invalid task id.</Message> : null}

      {isLoading ? <div>Loading task…</div> : null}
      {!isLoading ? (
        <TaskPeopleMonthlyLayout
          taskId={taskId}
          task={task}
          projectId={projectId}
          taskOrderId={taskOrderId}
          timecampTaskId={linkedTimecampTaskId}
          activeProviderName={activeProviderName}
          provider={activeProvider}
          providerLoading={providerLoading}
          autoPullMonthStart={autoPullMonthStart}
          taskForecastPath={taskForecastPath}
          actualsReturnPath={actualsReturnPath}
          onOpenTaskForecast={(path, state) => navigate(path, { state })}
        />
      ) : null}

      {isProjectLinkProviderActive ? (
        <ProviderLinkModal
          open={apiModalOpen}
          providerName={activeProviderName}
          providerDisplayName={activeProvider?.displayName}
          onClose={() => setApiModalOpen(false)}
          taskId={taskId}
          taskName={task?.task || null}
          onSaved={handleProviderLinkSaved}
        />
      ) : null}

      {isTimeCampActive ? (
        <Modal open={apiModalOpen} onClose={() => setApiModalOpen(false)} size="lg" overflow>
          <Modal.Header>
            <Modal.Title>Link TimeCamp task</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
              Choose the equivalent <b>TimeCamp task</b> that hours for <b>{task?.task ? task.task : 'this ProPlan task'}</b> will be logged under.
            </div>

            <Stack justifyContent="space-between" alignItems="center" wrap style={{ marginBottom: 12 }} spacing={10}>
              <div style={{ color: '#666', fontSize: 12 }}>
                Source: provider endpoint <b>/api/providers/timecamp/tasks</b> (configured via <b>/api/providers/active</b>)
              </div>
              <Button
                appearance="subtle"
                size="xs"
                onClick={handleFetchTimecampTasks}
                loading={timecampLoading}
                title="Refresh"
                aria-label="Refresh"
              >
                <i className="bi bi-arrow-clockwise" />
              </Button>
            </Stack>

            {timecampError ? (
              <Message type="error" showIcon style={{ marginBottom: 12 }}>
                {timecampError}
              </Message>
            ) : null}

            {timecampSaveError ? (
              <Message type="error" showIcon style={{ marginBottom: 12 }}>
                Failed to save mapping: {timecampSaveError}
              </Message>
            ) : null}

            {Array.isArray(timecampTasksTree) && timecampTasksTree.length > 0 ? (
              <div style={{ display: 'grid', gap: 12, marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Browse TimeCamp tasks (multi-level)</div>
                  <Cascader
                    searchable
                    block
                    parentSelectable
                    placeholder="Select the TimeCamp task…"
                    data={timecampTasksTree}
                    columnWidth={360}
                    popupStyle={{ minWidth: 920 }}
                    value={Number.isInteger(Number(timecampSelectedTaskId)) ? Number(timecampSelectedTaskId) : null}
                    onChange={(v) => {
                      const nextId = Number.isInteger(Number(v)) ? Number(v) : null;
                      if (Number(timecampSelectedTaskId) === Number(nextId)) return;
                      setTimecampSelectedTaskId(nextId);
                    }}
                    renderMenuItem={(label, item) => (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                        <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>#{item?.value}</span>
                      </span>
                    )}
                  />
                </div>

                <Panel bordered>
                  <div style={{ fontSize: 12, color: '#666', fontWeight: 700, marginBottom: 6 }}>Selected project/task</div>
                  {selectedTimecampNode ? (
                    <div style={{ display: 'grid', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 14, lineHeight: '18px' }}>{selectedTimecampNode.label}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>ID: {selectedTimecampNode.value}</div>
                      </div>

                      {selectedTimecampPath ? (
                        <div style={{ fontSize: 12, color: '#444' }}>
                          <div style={{ color: '#666', fontSize: 11, marginBottom: 2 }}>Path</div>
                          <div style={{ fontWeight: 700, wordBreak: 'break-word' }}>{selectedTimecampPath}</div>
                        </div>
                      ) : null}

                      {Number(timecampLastSavedTaskId) === Number(selectedTimecampNode.value) ? (
                        <div style={{ fontSize: 12, color: '#0a6b6c', fontWeight: 700 }}>Saved to task.</div>
                      ) : null}
                    </div>
                  ) : (
                    <div style={{ color: '#666', fontSize: 12 }}>Pick a TimeCamp item to map to this task.</div>
                  )}
                </Panel>
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <NsiButton onClick={() => setApiModalOpen(false)} disabled={timecampSaveLoading}>
              Close
            </NsiButton>
            <NsiButton
              variant="primary"
              loading={timecampSaveLoading}
              disabled={!canSaveTimecampSelection || timecampLoading}
              onClick={async () => {
                const id = Number(timecampSelectedTaskId);
                if (!Number.isInteger(id)) return;
                await saveTimecampSelection(id);
              }}
            >
              Save
            </NsiButton>
          </Modal.Footer>
        </Modal>
      ) : null}
    </div>
  );
}
